import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function EventsByYear() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [hoveredBar, setHoveredBar] = useState(null);
    
    // Cumulative fights state
    const [cumulativeData, setCumulativeData] = useState(null);
    const [cumulativeLoading, setCumulativeLoading] = useState(true);
    const [cumulativeError, setCumulativeError] = useState('');
    const [showDivisionsSeparate, setShowDivisionsSeparate] = useState(true);
    const [hoveredPoint, setHoveredPoint] = useState(null);
    
    // Events table state
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [minYear, setMinYear] = useState('2014');
    const [maxYear, setMaxYear] = useState('');
    const [appliedMinYear, setAppliedMinYear] = useState('2014');
    const [appliedMaxYear, setAppliedMaxYear] = useState('');
    
    // Upcoming events table state
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [upcomingEventsLoading, setUpcomingEventsLoading] = useState(false);
    const [upcomingSortConfig, setUpcomingSortConfig] = useState({ key: 'date', direction: 'asc' });
    
    // Elite fighters state
    const [eliteThresholds, setEliteThresholds] = useState({
        HW: 3, LHW: 3, MW: 5, WW: 9, LW: 10, FW: 9, BW: 8, FLW: 5,
                                W145: 2, W135: 2, W125: 2, W115: 2
    });
    const [appliedEliteThresholds, setAppliedEliteThresholds] = useState({
            HW: 3, LHW: 3, MW: 5, WW: 9, LW: 10, FW: 9, BW: 8, FLW: 5,
                                W145: 2, W135: 2, W125: 2, W115: 2
    });
    const [eliteFightersData, setEliteFightersData] = useState({});
    const [upcomingEliteFightersData, setUpcomingEliteFightersData] = useState({});
    const [highFightersData, setHighFightersData] = useState({});
    const [upcomingHighFightersData, setUpcomingHighFightersData] = useState({});
    const [showTableAsPercentage, setShowTableAsPercentage] = useState(false);
    
    // Timeline year filter state
    const [timelineMinYear, setTimelineMinYear] = useState('');
    const [timelineMaxYear, setTimelineMaxYear] = useState('');
    const [appliedTimelineMinYear, setAppliedTimelineMinYear] = useState('');
    const [appliedTimelineMaxYear, setAppliedTimelineMaxYear] = useState('');
    
    // Scatter plot year filter state
    const [scatterMinYear, setScatterMinYear] = useState('');
    const [scatterMaxYear, setScatterMaxYear] = useState('');
    const [appliedScatterMinYear, setAppliedScatterMinYear] = useState('');
    const [appliedScatterMaxYear, setAppliedScatterMaxYear] = useState('');
    
    // 3-month rolling elite fights state
    const [rollingEliteData, setRollingEliteData] = useState([]);
    const [eliteDisplayMode, setEliteDisplayMode] = useState('count'); // 'count' | 'fightRatio' | 'eventCount'
    const [eliteNumberedOnly, setEliteNumberedOnly] = useState(false);
    const [rollingEliteLoading, setRollingEliteLoading] = useState(false);
    const [rollingMinYear, setRollingMinYear] = useState('');
    const [rollingMaxYear, setRollingMaxYear] = useState('');
    const [appliedRollingMinYear, setAppliedRollingMinYear] = useState('');
    const [appliedRollingMaxYear, setAppliedRollingMaxYear] = useState('');
    const [hoveredRollingPoint, setHoveredRollingPoint] = useState(null);
    const [showAllTableEvents, setShowAllTableEvents] = useState(false);
    const [hoveredTimelinePoint, setHoveredTimelinePoint] = useState(null);
    
    // Scatter plot hover state
    const [hoveredScatterPoint, setHoveredScatterPoint] = useState(null);
    const [scatterTooltipPos, setScatterTooltipPos] = useState({ x: 0, y: 0 });
    
    // Column header tooltip state
    const [hoveredColumnHeader, setHoveredColumnHeader] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    
    // Numbered events filter state
    const [showNumberedEventsOnly, setShowNumberedEventsOnly] = useState(false);
    
    // Main card only filter state
    const [mainCardOnly, setMainCardOnly] = useState(false);

    // Expandable event details state
    const [expandedEvent, setExpandedEvent] = useState(null);
    const [expandedUpcomingEvent, setExpandedUpcomingEvent] = useState(null);
    const [eventFightsCache, setEventFightsCache] = useState({});
    const [eventFightsLoading, setEventFightsLoading] = useState(false);

    // Semester chart filter state
    const [semesterMinYear, setSemesterMinYear] = useState('');
    const [semesterMaxYear, setSemesterMaxYear] = useState('');
    const [appliedSemesterMinYear, setAppliedSemesterMinYear] = useState('');
    const [appliedSemesterMaxYear, setAppliedSemesterMaxYear] = useState('');
    
    // Title fights by year and division state
    const [titleFightsByYearAndDivision, setTitleFightsByYearAndDivision] = useState(null);
    const [titleFightsByYearLoading, setTitleFightsByYearLoading] = useState(false);
    const [titleFightsByYearError, setTitleFightsByYearError] = useState('');
    const [undisputedTitleFightsOnly, setUndisputedTitleFightsOnly] = useState(false);
    
    // Upset score (geometric mean of the winners' decimal odds) state
    const [upsetScoreData, setUpsetScoreData] = useState({});
    const [upsetCountData, setUpsetCountData] = useState({});
    const [upsetScoreLoading, setUpsetScoreLoading] = useState(false);
    
    // Mismatch (geometric mean of the favourites' decimal odds) state
    const [mismatchData, setMismatchData] = useState({});
    const [mismatchLoading, setMismatchLoading] = useState(false);

    // Recent form (card average of each fighter's win ratio over their last 5 fights) state
    const [recentFormData, setRecentFormData] = useState({});
    // Average win streak the fighters on the card are on coming into the event
    const [winStreakData, setWinStreakData] = useState({});
    const [upcomingRecentFormData, setUpcomingRecentFormData] = useState({});
    const [upcomingWinStreakData, setUpcomingWinStreakData] = useState({});
    // event_name -> { wins, fights } behind the L5 win %, shown so small samples stand out
    const [recentFormCounts, setRecentFormCounts] = useState({});
    const [upcomingRecentFormCounts, setUpcomingRecentFormCounts] = useState({});

    // Rank scores across the entire dataset (past + upcoming), used to color-code
    // a fight's rank score against all fights rather than just its own event
    const [allRankScores, setAllRankScores] = useState([]);
    const [allFightUpsetScores, setAllFightUpsetScores] = useState([]);

    useEffect(() => {
        fetchEventsTable();
    }, [mainCardOnly]);

    useEffect(() => {
        fetchUpsetScoreData();
    }, [mainCardOnly]);

    useEffect(() => {
        fetchMismatchData();
    }, [mainCardOnly]);

    useEffect(() => {
        fetchRecentFormData();
    }, [mainCardOnly]);

    useEffect(() => {
        fetchAllRankScores();
    }, [mainCardOnly]);

    useEffect(() => {
        fetchAllFightUpsetScores();
    }, [mainCardOnly]);

    // Clear event fights cache when mainCardOnly changes
    useEffect(() => {
        setEventFightsCache({});
        setExpandedEvent(null);
    }, [mainCardOnly]);

    useEffect(() => {
        fetchEventsData();
        fetchUpcomingEventsTable();
        fetchUpcomingRecentFormData();
        fetchCumulativeFightsData();
    }, []);

    useEffect(() => {
        fetchTitleFightsByYearAndDivision();
    }, [undisputedTitleFightsOnly]);

    // Fetch elite fighters percentage with default thresholds when events are loaded
    useEffect(() => {
        if (events.length > 0) {
            fetchEliteFightersPercentage();
            fetchHighFightersPercentage();
        }
    }, [events, appliedEliteThresholds]);

    // Fetch elite fighters for upcoming events when they're loaded or thresholds change
    useEffect(() => {
        if (upcomingEvents.length > 0) {
            fetchUpcomingEliteFightersPercentage();
            fetchUpcomingHighFightersPercentage();
        }
    }, [upcomingEvents, appliedEliteThresholds]);

    const fetchEventsData = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/api/events-by-year`);
            if (!res.ok) throw new Error('Failed to fetch events data');
            const result = await res.json();
            setData(result);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchEventsTable = async () => {
        setEventsLoading(true);
        try {
            const params = new URLSearchParams();
            if (mainCardOnly) {
                params.append('main_card_only', 'true');
            }
            const url = `${API_BASE}/api/ufc-events${params.toString() ? '?' + params.toString() : ''}`;
            const res = await fetch(url);
            if (!res.ok) throw new Error('Failed to fetch UFC events');
            const result = await res.json();
            setEvents(result);
        } catch (e) {
            console.error('Error fetching events table:', e.message);
        } finally {
            setEventsLoading(false);
        }
    };

    const fetchEventFights = async (eventName, isUpcoming = false) => {
        // Return early if already cached
        if (eventFightsCache[eventName]) {
            return;
        }

        setEventFightsLoading(true);
        try {
            const params = new URLSearchParams({ event_name: eventName });
            if (mainCardOnly) {
                params.append('main_card_only', 'true');
            }
            if (isUpcoming) {
                params.append('upcoming', 'true');
            }
            const res = await fetch(`${API_BASE}/api/event-fights?${params}`);
            if (!res.ok) throw new Error('Failed to fetch event fights');
            const result = await res.json();
            setEventFightsCache(prev => ({ ...prev, [eventName]: result }));
        } catch (e) {
            console.error('Error fetching event fights:', e.message);
        } finally {
            setEventFightsLoading(false);
        }
    };

    const fetchUpcomingEventsTable = async () => {
        setUpcomingEventsLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/upcoming-events`);
            if (!res.ok) throw new Error('Failed to fetch upcoming events');
            const result = await res.json();
            setUpcomingEvents(result);
        } catch (e) {
            console.error('Error fetching upcoming events table:', e.message);
        } finally {
            setUpcomingEventsLoading(false);
        }
    };

    const fetchCumulativeFightsData = async () => {
        setCumulativeLoading(true);
        setCumulativeError('');
        try {
            const res = await fetch(`${API_BASE}/api/cumulative-fights-by-year`);
            if (!res.ok) throw new Error('Failed to fetch cumulative fights data');
            const result = await res.json();
            setCumulativeData(result);
        } catch (e) {
            setCumulativeError(e.message);
        } finally {
            setCumulativeLoading(false);
        }
    };

    const fetchTitleFightsByYearAndDivision = async () => {
        setTitleFightsByYearLoading(true);
        setTitleFightsByYearError('');
        try {
            const params = new URLSearchParams();
            if (undisputedTitleFightsOnly) {
                params.append('undisputed_only', 'true');
            }
            const res = await fetch(`${API_BASE}/api/title-fights-by-year-and-division${params.toString() ? '?' + params.toString() : ''}`);
            if (!res.ok) throw new Error('Failed to fetch title fights data');
            const result = await res.json();
            setTitleFightsByYearAndDivision(result);
        } catch (e) {
            setTitleFightsByYearError(e.message);
        } finally {
            setTitleFightsByYearLoading(false);
        }
    };

    const fetchUpsetScoreData = async () => {
        setUpsetScoreLoading(true);
        try {
            const params = new URLSearchParams();
            if (mainCardOnly) {
                params.append('main_card_only', 'true');
            }
            const url = `${API_BASE}/api/upset-score${params.toString() ? '?' + params.toString() : ''}`;
            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch upset score data');
            const data = await response.json();
            // Create a map of event_name -> upset score (geometric mean of the winners' decimal odds)
            const upsetScoreMap = {};
            // ...and event_name -> number of fights won by the underdog
            const upsetCountMap = {};
            data.forEach(item => {
                upsetScoreMap[item.event_name] = item.upset_score;
                upsetCountMap[item.event_name] = item.upset_count;
            });
            setUpsetScoreData(upsetScoreMap);
            setUpsetCountData(upsetCountMap);
        } catch (e) {
            console.error('Error fetching upset score data:', e);
        } finally {
            setUpsetScoreLoading(false);
        }
    };

    const fetchMismatchData = async () => {
        setMismatchLoading(true);
        try {
            const params = new URLSearchParams();
            if (mainCardOnly) {
                params.append('main_card_only', 'true');
            }
            const url = `${API_BASE}/api/mismatch${params.toString() ? '?' + params.toString() : ''}`;
            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch mismatch data');
            const data = await response.json();
            // Create a map of event_name -> mismatch (geometric mean of the favourites' decimal odds)
            const mismatchMap = {};
            data.forEach(item => {
                mismatchMap[item.event_name] = item.mismatch;
            });
            setMismatchData(mismatchMap);
        } catch (e) {
            console.error('Error fetching mismatch data:', e);
        } finally {
            setMismatchLoading(false);
        }
    };

    const fetchRecentFormData = async () => {
        try {
            const params = new URLSearchParams();
            if (mainCardOnly) {
                params.append('main_card_only', 'true');
            }
            const url = `${API_BASE}/api/recent-form${params.toString() ? '?' + params.toString() : ''}`;
            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch recent form data');
            const data = await response.json();
            // Create a map of event_name -> average last-5 win ratio of the fighters on the card
            const recentFormMap = {};
            // ...and event_name -> average of the fighters' current win streaks
            const winStreakMap = {};
            const countsMap = {};
            data.forEach(item => {
                recentFormMap[item.event_name] = item.recent_form;
                winStreakMap[item.event_name] = item.win_streak_avg;
                countsMap[item.event_name] = { wins: item.win_count, fights: item.fight_count };
            });
            setRecentFormData(recentFormMap);
            setWinStreakData(winStreakMap);
            setRecentFormCounts(countsMap);
        } catch (e) {
            console.error('Error fetching recent form data:', e);
        }
    };

    // Upcoming fights have no fight_nb, so the whole card is always scored
    const fetchUpcomingRecentFormData = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/upcoming-recent-form`);
            if (!response.ok) throw new Error('Failed to fetch upcoming recent form data');
            const data = await response.json();
            const recentFormMap = {};
            const winStreakMap = {};
            const countsMap = {};
            data.forEach(item => {
                recentFormMap[item.event_name] = item.recent_form;
                winStreakMap[item.event_name] = item.win_streak_avg;
                countsMap[item.event_name] = { wins: item.win_count, fights: item.fight_count };
            });
            setUpcomingRecentFormData(recentFormMap);
            setUpcomingWinStreakData(winStreakMap);
            setUpcomingRecentFormCounts(countsMap);
        } catch (e) {
            console.error('Error fetching upcoming recent form data:', e);
        }
    };

    const fetchAllRankScores = async () => {
        try {
            const params = new URLSearchParams();
            if (mainCardOnly) {
                params.append('main_card_only', 'true');
            }
            const url = `${API_BASE}/api/all-rank-scores${params.toString() ? '?' + params.toString() : ''}`;
            const res = await fetch(url);
            if (!res.ok) throw new Error('Failed to fetch rank scores');
            const result = await res.json();
            setAllRankScores(result);
        } catch (e) {
            console.error('Error fetching rank scores:', e.message);
        }
    };

    const fetchAllFightUpsetScores = async () => {
        try {
            const params = new URLSearchParams();
            if (mainCardOnly) {
                params.append('main_card_only', 'true');
            }
            const url = `${API_BASE}/api/all-fight-upset-scores${params.toString() ? '?' + params.toString() : ''}`;
            const res = await fetch(url);
            if (!res.ok) throw new Error('Failed to fetch fight upset scores');
            const result = await res.json();
            setAllFightUpsetScores(result);
        } catch (e) {
            console.error('Error fetching fight upset scores:', e.message);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Only the sorted column shows an arrow, to keep headers narrow
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;

        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 4, fontSize: '0.75em', color: '#f5f5f5' }}>
                {sortConfig.direction === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    const columnDescriptions = {
        elite_percentage: 'Number of elite fights (see definition below)',
        high_percentage: 'Same as Elite Fights, with a 2x ranking threshold',
        strength_score: 'Strength of Fightmatrix ranks',
        upset_count: 'Number of fights won by the underdog',
        upset_score: 'Avg* odds of winners (higher = more upset-heavy)',
        mismatch: 'Avg* odds of favourites (higher = more balanced on paper)',
        recent_form: 'Total wins / total fights of the fighters on the card over their last 5 UFC fights each (debutants excluded)',
        win_streak_avg: 'Avg UFC win streak of the fighters on the card coming into the event (debutants count as 0)'
    };

    const handleHeaderMouseEnter = (e, columnKey) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPos({
            x: rect.left + rect.width / 2,
            y: rect.bottom + 5
        });
        setHoveredColumnHeader(columnKey);
    };

    // Determine if a fight is elite or high based on fighter ranks and thresholds
    const getFightEliteStatus = (fighter1Rank, fighter2Rank, fighter1WeightClass, fighter2WeightClass) => {
        const normalizeWeightClass = (wc) => {
            if (!wc) return null;
            // Map weight class to abbreviation if needed
            const wcMap = {
                'Heavyweight': 'HW',
                'Light Heavyweight': 'LHW',
                'Middleweight': 'MW',
                'Welterweight': 'WW',
                'Lightweight': 'LW',
                'Featherweight': 'FW',
                'Bantamweight': 'BW',
                'Flyweight': 'FLW',
                'Women\'s Featherweight': 'W145',
                'Women\'s Bantamweight': 'W135',
                'Women\'s Flyweight': 'W125',
                'Women\'s Strawweight': 'W115'
            };
            return wcMap[wc] || wc;
        };

        const f1Rank = parseFloat(fighter1Rank);
        const f2Rank = parseFloat(fighter2Rank);
        const f1WC = normalizeWeightClass(fighter1WeightClass);
        const f2WC = normalizeWeightClass(fighter2WeightClass);

        const f1EliteThreshold = appliedEliteThresholds[f1WC];
        const f2EliteThreshold = appliedEliteThresholds[f2WC];

        const f1IsElite = !isNaN(f1Rank) && f1EliteThreshold && f1Rank <= f1EliteThreshold;
        const f2IsElite = !isNaN(f2Rank) && f2EliteThreshold && f2Rank <= f2EliteThreshold;

        if (f1IsElite || f2IsElite) return 'Elite';

        const f1HighThreshold = f1EliteThreshold ? f1EliteThreshold * 2 : null;
        const f2HighThreshold = f2EliteThreshold ? f2EliteThreshold * 2 : null;

        const f1IsHigh = !isNaN(f1Rank) && f1HighThreshold && f1Rank <= f1HighThreshold;
        const f2IsHigh = !isNaN(f2Rank) && f2HighThreshold && f2Rank <= f2HighThreshold;

        if (f1IsHigh || f2IsHigh) return 'High';

        return '';
    };

    const fetchEliteFightersPercentage = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/event-elite-fighters`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ eliteThresholds: appliedEliteThresholds })
            });
            if (!res.ok) throw new Error('Failed to fetch elite fighters data');
            const result = await res.json();
            // Create maps for easy lookup by event name
            const percentageMap = {};
            const countMap = {};
            result.forEach(item => {
                percentageMap[item.event_name] = item.elite_percentage;
                countMap[item.event_name] = item.elite_fights_count;
            });
            setEliteFightersData({ percentages: percentageMap, counts: countMap });

            // Calculate rolling 1-year elite fights
            calculateRollingEliteFights(result);
        } catch (e) {
            console.error('Error fetching elite fighters data:', e.message);
        }
    };

    const fetchUpcomingEliteFightersPercentage = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/upcoming-event-elite-fighters`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ eliteThresholds: appliedEliteThresholds })
            });
            if (!res.ok) throw new Error('Failed to fetch upcoming elite fighters data');
            const result = await res.json();
            // Create maps for easy lookup by event name
            const percentageMap = {};
            const countMap = {};
            result.forEach(item => {
                percentageMap[item.event_name] = item.elite_percentage;
                countMap[item.event_name] = item.elite_fights_count;
            });
            setUpcomingEliteFightersData({ percentages: percentageMap, counts: countMap });
        } catch (e) {
            console.error('Error fetching upcoming elite fighters data:', e.message);
        }
    };

    const fetchHighFightersPercentage = async () => {
        try {
            // Create high thresholds (2x elite thresholds)
            const highThresholds = {};
            Object.entries(appliedEliteThresholds).forEach(([division, threshold]) => {
                highThresholds[division] = threshold * 2;
            });

            const res = await fetch(`${API_BASE}/api/event-elite-fighters`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ eliteThresholds: highThresholds })
            });
            if (!res.ok) throw new Error('Failed to fetch high fighters data');
            const result = await res.json();
            // Create maps for easy lookup by event name
            const percentageMap = {};
            const countMap = {};
            result.forEach(item => {
                percentageMap[item.event_name] = item.elite_percentage;
                countMap[item.event_name] = item.elite_fights_count;
            });
            setHighFightersData({ percentages: percentageMap, counts: countMap });
        } catch (e) {
            console.error('Error fetching high fighters data:', e.message);
        }
    };

    const fetchUpcomingHighFightersPercentage = async () => {
        try {
            // Create high thresholds (2x elite thresholds)
            const highThresholds = {};
            Object.entries(appliedEliteThresholds).forEach(([division, threshold]) => {
                highThresholds[division] = threshold * 2;
            });

            const res = await fetch(`${API_BASE}/api/upcoming-event-elite-fighters`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ eliteThresholds: highThresholds })
            });
            if (!res.ok) throw new Error('Failed to fetch upcoming high fighters data');
            const result = await res.json();
            // Create maps for easy lookup by event name
            const percentageMap = {};
            const countMap = {};
            result.forEach(item => {
                percentageMap[item.event_name] = item.elite_percentage;
                countMap[item.event_name] = item.elite_fights_count;
            });
            setUpcomingHighFightersData({ percentages: percentageMap, counts: countMap });
        } catch (e) {
            console.error('Error fetching upcoming high fighters data:', e.message);
        }
    };

    const calculateRollingEliteFights = (eliteData) => {
        // Create a map from event name to elite count
        const eliteCountMap = {};
        const totalFightsMap = {};
        eliteData.forEach(item => {
            eliteCountMap[item.event_name] = item.elite_fights_count || 0;
            totalFightsMap[item.event_name] = item.total_fights || 0;
        });

        // Get events sorted by date
        const eventsWithElite = events
            .map(event => ({
                ...event,
                date: new Date(event.date),
                eliteCount: eliteCountMap[event.event_name] || 0,
                totalFights: totalFightsMap[event.event_name] || 0
            }))
            .sort((a, b) => a.date - b.date);
        
        if (eventsWithElite.length === 0) {
            setRollingEliteData([]);
            return;
        }
        
        // Rolling 1-year window, stepped forward one quarter at a time.
        // Each point sums elite fights in the 12 months ending at a quarter's end.
        const firstDate = eventsWithElite[0].date;
        const lastDate = eventsWithElite[eventsWithElite.length - 1].date;
        const firstQuarterIdx = firstDate.getFullYear() * 4 + Math.floor(firstDate.getMonth() / 3);
        const lastQuarterIdx = lastDate.getFullYear() * 4 + Math.floor(lastDate.getMonth() / 3);
        
        const rollingPoints = [];
        // Start at the first quarter whose trailing window covers a full year of events
        for (let q = firstQuarterIdx + 3; q <= lastQuarterIdx; q++) {
            const year = Math.floor(q / 4);
            const quarter = (q % 4) + 1;
            const windowEnd = new Date(year, quarter * 3, 1); // exclusive: first day of next quarter
            const windowStart = new Date(year - 1, quarter * 3, 1);
            
            let eliteFights = 0;
            let totalFights = 0;
            // Per-event counts: average number of elite fights per event
            let eventEliteSum = 0;
            let eventCount = 0;
            let numberedEventEliteSum = 0;
            let numberedEventCount = 0;
            eventsWithElite.forEach(event => {
                if (event.date >= windowStart && event.date < windowEnd) {
                    eliteFights += event.eliteCount;
                    totalFights += event.totalFights;
                    if (event.totalFights > 0) {
                        eventEliteSum += event.eliteCount;
                        eventCount++;
                        if (event.is_numbered === 1 || event.is_numbered === true) {
                            numberedEventEliteSum += event.eliteCount;
                            numberedEventCount++;
                        }
                    }
                }
            });

            rollingPoints.push({
                date: new Date(windowEnd.getTime() - 1),
                year,
                quarter,
                windowStart,
                eliteCount: eliteFights,
                totalFights,
                eliteRatio: totalFights > 0 ? eliteFights / totalFights : 0,
                eventCount,
                eventEliteAvg: eventCount > 0 ? eventEliteSum / eventCount : null,
                numberedEventCount,
                numberedEventEliteAvg: numberedEventCount > 0 ? numberedEventEliteSum / numberedEventCount : null
            });
        }
        
        setRollingEliteData(rollingPoints);
    };

    const getScatterPlotEvents = () => {
        let filtered = [...events];
        
        // Apply scatter plot specific year filters
        if (appliedScatterMinYear) {
            filtered = filtered.filter(e => new Date(e.date).getFullYear() >= parseInt(appliedScatterMinYear));
        }
        if (appliedScatterMaxYear) {
            filtered = filtered.filter(e => new Date(e.date).getFullYear() <= parseInt(appliedScatterMaxYear));
        }
        
        return filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    const getSortedEvents = () => {
        let filtered = [...events];
        
        // Apply year filters
        if (appliedMinYear) {
            filtered = filtered.filter(e => new Date(e.date).getFullYear() >= parseInt(appliedMinYear));
        }
        if (appliedMaxYear) {
            filtered = filtered.filter(e => new Date(e.date).getFullYear() <= parseInt(appliedMaxYear));
        }
        
        // Apply numbered events filter
        if (showNumberedEventsOnly) {
            filtered = filtered.filter(e => e.is_numbered === 1 || e.is_numbered === true);
        }
        
        const sorted = filtered.sort((a, b) => {
            let comparison = 0;

            if (sortConfig.key === 'finish_percentage') {
                // Always sort by finish percentage
                const aValue = a.finish_percentage ?? 0;
                const bValue = b.finish_percentage ?? 0;
                comparison = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else if (sortConfig.key === 'elite_percentage') {
                // Always sort by elite count
                const aElite = eliteFightersData.counts ? (eliteFightersData.counts[a.event_name] ?? 0) : 0;
                const bElite = eliteFightersData.counts ? (eliteFightersData.counts[b.event_name] ?? 0) : 0;
                comparison = aElite > bElite ? 1 : aElite < bElite ? -1 : 0;
            } else if (sortConfig.key === 'high_percentage') {
                // Sort by high count
                const aHigh = highFightersData.counts ? (highFightersData.counts[a.event_name] ?? 0) : 0;
                const bHigh = highFightersData.counts ? (highFightersData.counts[b.event_name] ?? 0) : 0;
                comparison = aHigh > bHigh ? 1 : aHigh < bHigh ? -1 : 0;
            } else if (sortConfig.key === 'upset_count') {
                // Sort by number of upsets
                const aUpsets = upsetCountData[a.event_name] ?? 0;
                const bUpsets = upsetCountData[b.event_name] ?? 0;
                comparison = aUpsets > bUpsets ? 1 : aUpsets < bUpsets ? -1 : 0;
            } else if (sortConfig.key === 'upset_score') {
                // Sort by upset score
                const aPred = upsetScoreData[a.event_name] ?? 0;
                const bPred = upsetScoreData[b.event_name] ?? 0;
                comparison = aPred > bPred ? 1 : aPred < bPred ? -1 : 0;
            } else if (sortConfig.key === 'mismatch') {
                // Sort by mismatch (favourites' odds)
                const aMismatch = mismatchData[a.event_name] ?? 0;
                const bMismatch = mismatchData[b.event_name] ?? 0;
                comparison = aMismatch > bMismatch ? 1 : aMismatch < bMismatch ? -1 : 0;
            } else if (sortConfig.key === 'recent_form') {
                // Sort by the card's average last-5 win ratio
                const aForm = recentFormData[a.event_name] ?? 0;
                const bForm = recentFormData[b.event_name] ?? 0;
                comparison = aForm > bForm ? 1 : aForm < bForm ? -1 : 0;
            } else if (sortConfig.key === 'win_streak_avg') {
                // Sort by the average of the fighters' current win streaks
                const aStreak = winStreakData[a.event_name] ?? 0;
                const bStreak = winStreakData[b.event_name] ?? 0;
                comparison = aStreak > bStreak ? 1 : aStreak < bStreak ? -1 : 0;
            } else {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (aValue == null || bValue == null) {
                    return 0;
                }

                if (typeof aValue === 'string') {
                    comparison = aValue.localeCompare(bValue);
                } else {
                    comparison = aValue > bValue ? 1 : -1;
                }
            }

            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
        return sorted;
    };

    const getMedianStrengthScoreByEventType = (isNumbered) => {
        let filtered = [...events];
        
        // Apply year filters
        if (appliedMinYear) {
            filtered = filtered.filter(e => new Date(e.date).getFullYear() >= parseInt(appliedMinYear));
        }
        if (appliedMaxYear) {
            filtered = filtered.filter(e => new Date(e.date).getFullYear() <= parseInt(appliedMaxYear));
        }
        
        // Filter by event type
        const typeFiltered = filtered.filter(e => {
            const isNumEvent = e.is_numbered === 1 || e.is_numbered === true;
            return isNumbered ? isNumEvent : !isNumEvent;
        });
        
        if (typeFiltered.length === 0) return null;
        
        const scores = typeFiltered
            .filter(e => e.strength_score !== null && e.strength_score !== undefined && !isNaN(e.strength_score))
            .map(e => e.strength_score)
            .sort((a, b) => a - b);
        
        if (scores.length === 0) return null;
        
        const mid = Math.floor(scores.length / 2);
        return scores.length % 2 !== 0 ? scores[mid] : (scores[mid - 1] + scores[mid]) / 2;
    };

    // Helper functions for percentile calculation and coloring
    // goldStart: percentile where white turns to gold (75 by default, higher for heavy-tailed measures)
    const getPercentileColor = (percentile, goldStart = 75) => {
        if (percentile === null) return '#999';
        if (percentile < goldStart) {
            // White gradient from 0% to goldStart
            const alpha = 0.2 + (percentile / goldStart) * 0.7; // 0.2 at 0%, 0.9 at goldStart
            return `rgba(255, 255, 255, ${alpha.toFixed(2)})`;
        } else {
            // Blend from white to golden above goldStart
            const ratio = (percentile - goldStart) / (100 - goldStart); // 0 at goldStart, 1 at 100%
            const r = 255;
            const g = Math.round(255 - ratio * 40); // 255 at 75%, 215 at 100%
            const b = Math.round(255 * (1 - ratio)); // 255 at 75%, 0 at 100%
            const alpha = 0.9 + ratio * 0.1; // 0.9 at 75%, 1.0 at 100%
            return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
        }
    };

    // Wins / fights behind an L5 win %, so a small sample is visible next to the percentage
    const renderL5Counts = (counts) => {
        if (!counts || !counts.fights) return null;
        return (
            <div style={{ fontSize: '0.75em', color: '#888' }}>{counts.wins}/{counts.fights}</div>
        );
    };

    const calculatePercentile = (value, allValues) => {
        if (value === null || value === undefined || isNaN(value)) return null;
        const sorted = allValues
            .filter(v => v !== null && v !== undefined && !isNaN(v))
            .sort((a, b) => a - b);
        if (sorted.length === 0) return null;
        const count = sorted.filter(v => v < value).length;
        return Math.round((count / sorted.length) * 100);
    };

    const getAverageStats = (isNumbered) => {
        let filtered = [...events];
        
        // Apply year filters
        if (appliedMinYear) {
            filtered = filtered.filter(e => new Date(e.date).getFullYear() >= parseInt(appliedMinYear));
        }
        if (appliedMaxYear) {
            filtered = filtered.filter(e => new Date(e.date).getFullYear() <= parseInt(appliedMaxYear));
        }
        
        // Filter by event type
        const typeFiltered = filtered.filter(e => {
            const isNumEvent = e.is_numbered === 1 || e.is_numbered === true;
            return isNumEvent === isNumbered;
        });
        
        if (typeFiltered.length === 0) {
            return null;
        }
        
        const avg_fights = typeFiltered.reduce((sum, e) => sum + (e.number_of_fights || 0), 0) / typeFiltered.length;
        const avg_titles = typeFiltered.reduce((sum, e) => sum + (e.title_fight_count || 0), 0) / typeFiltered.length;
        const avg_finish = typeFiltered.reduce((sum, e) => sum + (e.finish_percentage || 0), 0) / typeFiltered.length;
        const avg_strength = typeFiltered.reduce((sum, e) => sum + (e.strength_score || 0), 0) / typeFiltered.length;
        
        // Average elite count
        const elite_counts = typeFiltered
            .map(e => eliteFightersData.counts && eliteFightersData.counts[e.event_name] || 0)
            .filter(c => c !== null && c !== undefined);
        const avg_elite = elite_counts.length > 0 ? elite_counts.reduce((sum, c) => sum + c, 0) / elite_counts.length : null;
        
        // Average high count
        const high_counts = typeFiltered
            .map(e => highFightersData.counts && highFightersData.counts[e.event_name] || 0)
            .filter(c => c !== null && c !== undefined);
        const avg_high = high_counts.length > 0 ? high_counts.reduce((sum, c) => sum + c, 0) / high_counts.length : null;
        
        return {
            number_of_fights: avg_fights,
            title_fight_count: avg_titles,
            finish_percentage: avg_finish,
            elite_count: avg_elite,
            high_count: avg_high,
            strength_score: avg_strength
        };
    };

    // Get all strength scores from both upcoming and past events for combined percentile calculation
    const getCombinedStrengthScoreValues = () => {
        const allStrengthScores = [];
        
        // Add strength scores from past events
        if (events && events.length > 0) {
            events.forEach(e => {
                if (e.strength_score !== null && e.strength_score !== undefined && !isNaN(e.strength_score)) {
                    allStrengthScores.push(e.strength_score);
                }
            });
        }
        
        // Add strength scores from upcoming events
        if (upcomingEvents && upcomingEvents.length > 0) {
            upcomingEvents.forEach(e => {
                if (e.strength_score !== null && e.strength_score !== undefined && !isNaN(e.strength_score)) {
                    allStrengthScores.push(e.strength_score);
                }
            });
        }
        
        return allStrengthScores;
    };

    // Chart dimensions and padding
    const width = 900;
    const padding = { top: 40, right: 40, bottom: 60, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    
    // Calculate max count to determine chart height
    const maxCount = data.length > 0 ? Math.max(...data.map(d => d.event_count)) : 0;
    const maxScale = 40;
    const neededHeight = maxCount > maxScale ? (maxCount / maxScale) * 400 + 100 : 500;
    const height = Math.max(neededHeight, 500);
    const chartHeight = height - padding.top - padding.bottom;

    // Scale functions
    const getX = (index) => {
        const barWidth = chartWidth / data.length;
        return padding.left + index * barWidth;
    };

    const getBarWidth = () => {
        if (data.length === 0) return 0;
        const barWidth = chartWidth / data.length;
        // Leave some gap between bars
        return Math.max(barWidth * 0.8, 1);
    };

    const getY = (count) => {
        const maxScale = 40;
        const scale = Math.max(maxScale, maxCount);
        return padding.top + chartHeight - (count / scale) * chartHeight;
    };

    const getBarHeight = (count) => {
        if (maxCount === 0) return 0;
        return (count / maxCount) * chartHeight;
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <h1 style={styles.title}>UFC Events by Year</h1>
                <div style={styles.loading}>Loading data...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <h1 style={styles.title}>UFC Events by Year</h1>
                <div style={styles.error}>Error: {error}</div>
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div style={styles.container}>
                <h1 style={styles.title}>UFC Events by Year</h1>
                <div style={styles.error}>No data available</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Upcoming Events Table */}
            <div style={styles.tableContainer}>
                <h2 style={styles.tableTitle}>Upcoming UFC Events</h2>
                
                {upcomingEventsLoading ? (
                    <div style={styles.loading}>Loading upcoming events...</div>
                ) : (
                    <>
                        {upcomingEvents && upcomingEvents.length > 0 ? (
                            <div style={styles.tableWrapper}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr style={styles.tableHeader}>
                                            <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => setUpcomingSortConfig({ key: 'event_name', direction: upcomingSortConfig.key === 'event_name' && upcomingSortConfig.direction === 'asc' ? 'desc' : 'asc' })} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                                Event Name
                                            </th>
                                            <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => setUpcomingSortConfig({ key: 'date', direction: upcomingSortConfig.key === 'date' && upcomingSortConfig.direction === 'asc' ? 'desc' : 'asc' })} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                                Date {(() => {
                                                    const activeColor = '#f5f5f5';
                                                    const inactiveColor = '#bdbdbd';
                                                    const isActive = upcomingSortConfig.key === 'date';
                                                    const direction = isActive ? upcomingSortConfig.direction : null;
                                                    return <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: '0.75em', color: direction === 'asc' ? activeColor : direction === 'desc' ? activeColor : inactiveColor, opacity: isActive ? 1 : 0.55 }}>{direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↑↓'}</span>;
                                                })()}
                                            </th>
                                            <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => setUpcomingSortConfig({ key: 'number_of_fights', direction: upcomingSortConfig.key === 'number_of_fights' && upcomingSortConfig.direction === 'asc' ? 'desc' : 'asc' })} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                                Fights {(() => {
                                                    const activeColor = '#f5f5f5';
                                                    const inactiveColor = '#bdbdbd';
                                                    const isActive = upcomingSortConfig.key === 'number_of_fights';
                                                    const direction = isActive ? upcomingSortConfig.direction : null;
                                                    return <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: '0.75em', color: direction === 'asc' ? activeColor : direction === 'desc' ? activeColor : inactiveColor, opacity: isActive ? 1 : 0.55 }}>{direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↑↓'}</span>;
                                                })()}
                                            </th>
                                            <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => setUpcomingSortConfig({ key: 'elite_count', direction: upcomingSortConfig.key === 'elite_count' && upcomingSortConfig.direction === 'asc' ? 'desc' : 'asc' })} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'elite_percentage'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                                Elite<br />Fights {(() => {
                                                    const activeColor = '#f5f5f5';
                                                    const inactiveColor = '#bdbdbd';
                                                    const isActive = upcomingSortConfig.key === 'elite_count';
                                                    const direction = isActive ? upcomingSortConfig.direction : null;
                                                    return <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: '0.75em', color: direction === 'asc' ? activeColor : direction === 'desc' ? activeColor : inactiveColor, opacity: isActive ? 1 : 0.55 }}>{direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↑↓'}</span>;
                                                })()}
                                            </th>
                                            <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => setUpcomingSortConfig({ key: 'high_count', direction: upcomingSortConfig.key === 'high_count' && upcomingSortConfig.direction === 'asc' ? 'desc' : 'asc' })} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'high_percentage'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                                Sub-Elite<br />Fights {(() => {
                                                    const activeColor = '#f5f5f5';
                                                    const inactiveColor = '#bdbdbd';
                                                    const isActive = upcomingSortConfig.key === 'high_count';
                                                    const direction = isActive ? upcomingSortConfig.direction : null;
                                                    return <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: '0.75em', color: direction === 'asc' ? activeColor : direction === 'desc' ? activeColor : inactiveColor, opacity: isActive ? 1 : 0.55 }}>{direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↑↓'}</span>;
                                                })()}
                                            </th>
                                            <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => setUpcomingSortConfig({ key: 'strength_score', direction: upcomingSortConfig.key === 'strength_score' && upcomingSortConfig.direction === 'asc' ? 'desc' : 'asc' })} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'strength_score'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                                Ranks Score {(() => {
                                                    const activeColor = '#f5f5f5';
                                                    const inactiveColor = '#bdbdbd';
                                                    const isActive = upcomingSortConfig.key === 'strength_score';
                                                    const direction = isActive ? upcomingSortConfig.direction : null;
                                                    return <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: '0.75em', color: direction === 'asc' ? activeColor : direction === 'desc' ? activeColor : inactiveColor, opacity: isActive ? 1 : 0.55 }}>{direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↑↓'}</span>;
                                                })()}
                                            </th>
                                            <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => setUpcomingSortConfig({ key: 'recent_form', direction: upcomingSortConfig.key === 'recent_form' && upcomingSortConfig.direction === 'asc' ? 'desc' : 'asc' })} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'recent_form'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                                L5 Win % {(() => {
                                                    const activeColor = '#f5f5f5';
                                                    const inactiveColor = '#bdbdbd';
                                                    const isActive = upcomingSortConfig.key === 'recent_form';
                                                    const direction = isActive ? upcomingSortConfig.direction : null;
                                                    return <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: '0.75em', color: direction === 'asc' ? activeColor : direction === 'desc' ? activeColor : inactiveColor, opacity: isActive ? 1 : 0.55 }}>{direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↑↓'}</span>;
                                                })()}
                                            </th>
                                            <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => setUpcomingSortConfig({ key: 'win_streak_avg', direction: upcomingSortConfig.key === 'win_streak_avg' && upcomingSortConfig.direction === 'asc' ? 'desc' : 'asc' })} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'win_streak_avg'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                                Avg Win Streak {(() => {
                                                    const activeColor = '#f5f5f5';
                                                    const inactiveColor = '#bdbdbd';
                                                    const isActive = upcomingSortConfig.key === 'win_streak_avg';
                                                    const direction = isActive ? upcomingSortConfig.direction : null;
                                                    return <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: '0.75em', color: direction === 'asc' ? activeColor : direction === 'desc' ? activeColor : inactiveColor, opacity: isActive ? 1 : 0.55 }}>{direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↑↓'}</span>;
                                                })()}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const sortedUpcoming = [...upcomingEvents].sort((a, b) => {
                                                let comparison = 0;
                                                const upcomingValueMaps = { recent_form: upcomingRecentFormData, win_streak_avg: upcomingWinStreakData };
                                                const valueMap = upcomingValueMaps[upcomingSortConfig.key];
                                                const aValue = valueMap ? valueMap[a.event_name] : a[upcomingSortConfig.key];
                                                const bValue = valueMap ? valueMap[b.event_name] : b[upcomingSortConfig.key];

                                                if (aValue == null || bValue == null) {
                                                    return 0;
                                                }

                                                if (typeof aValue === 'string') {
                                                    comparison = aValue.localeCompare(bValue);
                                                } else {
                                                    comparison = aValue > bValue ? 1 : -1;
                                                }

                                                return upcomingSortConfig.direction === 'asc' ? comparison : -comparison;
                                            });

                                            // Calculate percentiles relative to combined upcoming + past events
                                            const combinedStrengthScores = getCombinedStrengthScoreValues();
                                            const combinedRecentFormValues = [...Object.values(recentFormData), ...Object.values(upcomingRecentFormData)].filter(v => v !== null && v !== undefined);
                                            const combinedWinStreakValues = [...Object.values(winStreakData), ...Object.values(upcomingWinStreakData)].filter(v => v !== null && v !== undefined);

                                            return sortedUpcoming.map((event, idx) => {
                                                const strengthPercentile = calculatePercentile(event.strength_score, combinedStrengthScores);
                                                const strengthColor = getPercentileColor(strengthPercentile);
                                                const strengthScoreColorPercentile = strengthPercentile;
                                                
                                                // Calculate elite count percentiles relative to combined upcoming + past events
                                                const combinedEliteCounts = [];
                                                if (events && events.length > 0) {
                                                    events.forEach(e => {
                                                        if (eliteFightersData.counts && eliteFightersData.counts[e.event_name] !== undefined) {
                                                            combinedEliteCounts.push(eliteFightersData.counts[e.event_name]);
                                                        }
                                                    });
                                                }
                                                if (upcomingEvents && upcomingEvents.length > 0) {
                                                    upcomingEvents.forEach(e => {
                                                        if (upcomingEliteFightersData.counts && upcomingEliteFightersData.counts[e.event_name] !== undefined) {
                                                            combinedEliteCounts.push(upcomingEliteFightersData.counts[e.event_name]);
                                                        }
                                                    });
                                                }
                                                const elitePercentile = upcomingEliteFightersData.counts && upcomingEliteFightersData.counts[event.event_name] !== undefined 
                                                    ? calculatePercentile(upcomingEliteFightersData.counts[event.event_name], combinedEliteCounts)
                                                    : null;
                                                const eliteColor = getPercentileColor(elitePercentile);
                                                
                                                // Calculate high count percentiles relative to combined upcoming + past events
                                                const combinedHighCounts = [];
                                                if (events && events.length > 0) {
                                                    events.forEach(e => {
                                                        if (highFightersData.counts && highFightersData.counts[e.event_name] !== undefined) {
                                                            combinedHighCounts.push(highFightersData.counts[e.event_name]);
                                                        }
                                                    });
                                                }
                                                if (upcomingEvents && upcomingEvents.length > 0) {
                                                    upcomingEvents.forEach(e => {
                                                        if (upcomingHighFightersData.counts && upcomingHighFightersData.counts[e.event_name] !== undefined) {
                                                            combinedHighCounts.push(upcomingHighFightersData.counts[e.event_name]);
                                                        }
                                                    });
                                                }
                                                const highPercentile = upcomingHighFightersData.counts && upcomingHighFightersData.counts[event.event_name] !== undefined 
                                                    ? calculatePercentile(upcomingHighFightersData.counts[event.event_name], combinedHighCounts)
                                                    : null;
                                                const highColor = getPercentileColor(highPercentile);

                                                const recentForm = upcomingRecentFormData[event.event_name];
                                                const recentFormColor = recentForm !== undefined && recentForm !== null ? getPercentileColor(calculatePercentile(recentForm, combinedRecentFormValues)) : undefined;
                                                const winStreak = upcomingWinStreakData[event.event_name];
                                                const winStreakColor = winStreak !== undefined && winStreak !== null ? getPercentileColor(calculatePercentile(winStreak, combinedWinStreakValues)) : undefined;
                                                
                                                return (
                                                    <>
                                                    <tr key={idx} style={{...styles.tableRow, cursor: 'pointer'}} onClick={() => {
                                                        if (expandedUpcomingEvent === event.event_name) {
                                                            setExpandedUpcomingEvent(null);
                                                        } else {
                                                            setExpandedUpcomingEvent(event.event_name);
                                                            fetchEventFights(event.event_name, true);
                                                        }
                                                    }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333c4d'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                                        <td style={styles.eventNameCell}>
                                                            {event.event_url ? (
                                                                <a href={event.event_url} target="_blank" rel="noopener noreferrer" style={styles.link}>
                                                                    {event.event_name}
                                                                </a>
                                                            ) : (
                                                                event.event_name
                                                            )}
                                                        </td>
                                                        <td style={styles.transparentCell}>{new Date(event.date).toLocaleDateString(undefined, { year: '2-digit', month: 'numeric', day: 'numeric' })}</td>
                                                        <td style={styles.transparentCell}>{event.number_of_fights}</td>
                                                        <td style={{...styles.tableCell, color: eliteColor}}>
                                                            {upcomingEliteFightersData.counts && upcomingEliteFightersData.counts[event.event_name] !== undefined ? upcomingEliteFightersData.counts[event.event_name] : ''}
                                                        </td>
                                                        <td style={{...styles.tableCell, color: highColor}}>
                                                            {upcomingHighFightersData.counts && upcomingHighFightersData.counts[event.event_name] !== undefined ? upcomingHighFightersData.counts[event.event_name] : ''}
                                                        </td>
                                                        <td style={{ ...styles.tableCell, color: strengthColor }}>
                                                            {event.strength_score !== null && event.strength_score !== undefined ? event.strength_score.toFixed(0) : ''}
                                                        </td>
                                                        <td style={{ ...styles.tableCell, color: recentFormColor }}>
                                                            {recentForm !== undefined && recentForm !== null ? Math.round(recentForm * 100) + '%' : ''}
                                                            {renderL5Counts(upcomingRecentFormCounts[event.event_name])}
                                                        </td>
                                                        <td style={{ ...styles.tableCell, color: winStreakColor }}>
                                                            {winStreak !== undefined && winStreak !== null ? winStreak.toFixed(2) : ''}
                                                        </td>
                                                    </tr>
                                                    {expandedUpcomingEvent === event.event_name && (
                                                        <tr key={`expanded-upcoming-${idx}`} style={{ background: '#0f0f0f' }}>
                                                            <td colSpan={8} style={{ padding: '12px 0 12px 12px', border: 'none', borderBottom: '1px solid #444' }}>
                                                                {eventFightsLoading && !eventFightsCache[event.event_name] ? (
                                                                    <div style={{ color: '#888', padding: '20px', textAlign: 'center' }}>Loading fight details...</div>
                                                                ) : eventFightsCache[event.event_name] && eventFightsCache[event.event_name].length > 0 ? (
                                                                    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                                                        <thead>
                                                                            <tr style={{ borderBottom: '1px solid #555' }}>
                                                                                <th style={{ textAlign: 'left', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Fighter 1</th>
                                                                                <th style={{ textAlign: 'left', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Fighter 2</th>
                                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Rank 1</th>
                                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Rank 2</th>
                                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Class 1</th>
                                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Class 2</th>
                                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Rank Score</th>
                                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}></th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {(() => {
                                                                                const fights = eventFightsCache[event.event_name] || [];
                                                                                return fights.map((fight, fightIdx) => {
                                                                                    const rankScorePercentile = fight.rank_score !== null
                                                                                        ? calculatePercentile(fight.rank_score, allRankScores)
                                                                                        : null;
                                                                                    return (
                                                                                    <tr key={fightIdx} style={{ borderBottom: '1px solid #333' }}>
                                                                                        <td style={{ padding: '8px', color: '#ddd' }}>{fight.fighter1}</td>
                                                                                        <td style={{ padding: '8px', color: '#ddd' }}>{fight.fighter2}</td>
                                                                                        <td style={{ padding: '8px', textAlign: 'center', color: '#ccc' }}>{fight.fighter1_rank ?? ''}</td>
                                                                                        <td style={{ padding: '8px', textAlign: 'center', color: '#ccc' }}>{fight.fighter2_rank ?? ''}</td>
                                                                                        <td style={{ padding: '8px', textAlign: 'center', color: '#ccc' }}>{fight.fighter1_weight_class ?? ''}</td>
                                                                                        <td style={{ padding: '8px', textAlign: 'center', color: '#ccc' }}>{fight.fighter2_weight_class ?? ''}</td>
                                                                                        <td style={{ padding: '8px', textAlign: 'center', color: rankScorePercentile !== null ? getPercentileColor(rankScorePercentile) : '#ddd' }}>{fight.rank_score !== null ? fight.rank_score.toFixed(1) : ''}</td>
                                                                                    <td style={{ padding: '8px', textAlign: 'center', color: '#ddd' }}>
                                                                                        {getFightEliteStatus(fight.fighter1_rank, fight.fighter2_rank, fight.fighter1_weight_class, fight.fighter2_weight_class) === 'Elite' ? (
                                                                                            <span style={{ color: '#FFD700' }}>Elite</span>
                                                                                        ) : getFightEliteStatus(fight.fighter1_rank, fight.fighter2_rank, fight.fighter1_weight_class, fight.fighter2_weight_class) === 'High' ? (
                                                                                            <span style={{ color: '#FFA500' }}>Sub-Elite</span>
                                                                                        ) : null}
                                                                                    </td>
                                                                                </tr>
                                                                                    );
                                                                                });
                                                                            })()}
                                                                        </tbody>
                                                                    </table>
                                                                ) : (
                                                                    <div style={{ color: '#888', padding: '20px', textAlign: 'center' }}>No fight details available</div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                    </>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={styles.loading}>No upcoming events available</div>
                        )}
                    </>
                )}
            </div>

            <div style={styles.tableContainer}>
                <h2 style={styles.tableTitle}>Past UFC Events</h2>
                
                {/* Year Filter */}
                <div style={styles.filterContainer}>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Min Year:</label>
                        <input
                            type="number"
                            value={minYear}
                            onChange={(e) => setMinYear(e.target.value)}
                            placeholder=""
                            style={styles.filterInput}
                        />
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Max Year:</label>
                        <input
                            type="number"
                            value={maxYear}
                            onChange={(e) => setMaxYear(e.target.value)}
                            placeholder=""
                            style={styles.filterInput}
                        />
                    </div>
                    <button
                        onClick={() => {
                            setAppliedMinYear(minYear);
                            setAppliedMaxYear(maxYear);
                        }}
                        style={styles.applyButton}
                    >
                        Apply
                    </button>
                    {(appliedMinYear || appliedMaxYear) && (
                        <button
                            onClick={() => {
                                setMinYear('');
                                setMaxYear('');
                                setAppliedMinYear('');
                                setAppliedMaxYear('');
                            }}
                            style={styles.clearButton}
                        >
                            Clear Filters
                        </button>
                    )}
                    <div style={{...styles.filterGroup, marginLeft: 'auto'}}>
                        <label style={styles.filterLabel}>Numbered Events Only</label>
                        <div 
                            style={{
                                width: '32px',
                                height: '20px',
                                background: showNumberedEventsOnly ? '#64B5F6' : '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555',
                                cursor: 'pointer'
                            }}
                            onClick={() => setShowNumberedEventsOnly(!showNumberedEventsOnly)}
                        >
                            <div style={{
                                width: '16px',
                                height: '16px',
                                background: '#fff',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '2px',
                                left: showNumberedEventsOnly ? '14px' : '2px',
                                transition: 'all 0.25s ease'
                            }} />
                        </div>
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Main Card Only</label>
                        <div 
                            style={{
                                width: '32px',
                                height: '20px',
                                background: mainCardOnly ? '#64B5F6' : '#444',
                                borderRadius: '10px',
                                position: 'relative',
                                transition: 'all 0.25s ease',
                                border: '1px solid #555',
                                cursor: 'pointer'
                            }}
                            onClick={() => setMainCardOnly(!mainCardOnly)}
                        >
                            <div style={{
                                width: '16px',
                                height: '16px',
                                background: '#fff',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '2px',
                                left: mainCardOnly ? '14px' : '2px',
                                transition: 'all 0.25s ease'
                            }} />
                        </div>
                    </div>

                </div>
                
                {eventsLoading ? (
                    <div style={styles.loading}>Loading events table...</div>
                ) : (
                    <>
                        <div style={styles.tableWrapper}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.tableHeader}>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, width: '1%', padding: '6px 4px' }}>
                                        #
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => handleSort('event_name')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'event_name'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        Event Name
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => handleSort('date')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'date'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        Date {getSortIcon('date')}
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => handleSort('number_of_fights')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'number_of_fights'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        Fights {getSortIcon('number_of_fights')}
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => handleSort('finish_percentage')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'finish_percentage'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        Finish<br />Rate {getSortIcon('finish_percentage')}
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer', padding: '6px 6px' }} onClick={() => handleSort('title_fight_count')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'title_fight_count'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        Title<br />Fights {getSortIcon('title_fight_count')}
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer', padding: '6px 6px' }} onClick={() => handleSort('elite_percentage')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'elite_percentage'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        Elite<br />Fights {getSortIcon('elite_percentage')}
                                    </th>
                                    <th
                                        style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer', padding: '6px 6px' }}
                                        onClick={() => handleSort('high_percentage')}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'high_percentage'); }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}
                                    >
                                        Sub-Elite<br />Fights {getSortIcon('high_percentage')}
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => handleSort('strength_score')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'strength_score'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        Ranks<br />Score {getSortIcon('strength_score')}
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => handleSort('upset_count')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'upset_count'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        Upsets {getSortIcon('upset_count')}
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => handleSort('upset_score')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'upset_score'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        Win.<br />Odds {getSortIcon('upset_score')}
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => handleSort('mismatch')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'mismatch'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        Fav.<br />Odds {getSortIcon('mismatch')}
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => handleSort('recent_form')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'recent_form'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        L5<br />Win % {getSortIcon('recent_form')}
                                    </th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, cursor: 'pointer' }} onClick={() => handleSort('win_streak_avg')} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#333'; handleHeaderMouseEnter(e, 'win_streak_avg'); }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; setHoveredColumnHeader(null); }}>
                                        Avg Win<br />Streak {getSortIcon('win_streak_avg')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const sortedEvents = getSortedEvents();
                                    const displayedEvents = showAllTableEvents ? sortedEvents : sortedEvents.slice(0, 20);
                                    
                                    // Calculate percentile arrays for coloring
                                    // Colors are based on filtered events (sortedEvents)
                                    const colorFinishValues = sortedEvents
                                        .filter(e => e.finish_percentage !== undefined && !isNaN(e.finish_percentage))
                                        .map(e => e.finish_percentage);
                                    const colorEliteValues = sortedEvents
                                        .filter(e => eliteFightersData.percentages && eliteFightersData.percentages[e.event_name] !== undefined)
                                        .map(e => eliteFightersData.counts[e.event_name]);
                                    const colorHighValues = sortedEvents
                                        .filter(e => highFightersData.percentages && highFightersData.percentages[e.event_name] !== undefined)
                                        .map(e => highFightersData.counts[e.event_name]);
                                    const colorStrengthScoreValues = sortedEvents
                                        .filter(e => e.strength_score !== null && e.strength_score !== undefined && !isNaN(e.strength_score))
                                        .map(e => e.strength_score);
                                    const colorUpsetCountValues = sortedEvents
                                        .filter(e => upsetCountData[e.event_name] !== undefined && upsetCountData[e.event_name] !== null)
                                        .map(e => upsetCountData[e.event_name]);
                                    const colorUpsetScoreValues = sortedEvents
                                        .filter(e => upsetScoreData[e.event_name] !== undefined && upsetScoreData[e.event_name] !== null)
                                        .map(e => upsetScoreData[e.event_name]);
                                    const colorMismatchValues = sortedEvents
                                        .filter(e => mismatchData[e.event_name] !== undefined && mismatchData[e.event_name] !== null)
                                        .map(e => mismatchData[e.event_name]);
                                    const colorRecentFormValues = sortedEvents
                                        .filter(e => recentFormData[e.event_name] !== undefined && recentFormData[e.event_name] !== null)
                                        .map(e => recentFormData[e.event_name]);
                                    const colorWinStreakValues = sortedEvents
                                        .filter(e => winStreakData[e.event_name] !== undefined && winStreakData[e.event_name] !== null)
                                        .map(e => winStreakData[e.event_name]);

                                    // Calculate percentile arrays for strength score display
                                    // Display values are based on COMBINED upcoming + past events
                                    const allEventsStrengthScoreValues = getCombinedStrengthScoreValues();
                                    
                                    // Build array of rows with average rows first
                                    const allRows = [];
                                    
                                    // Add average rows for numbered and non-numbered events
                                    [true, false].forEach((isNumbered) => {
                                        const avgStats = getAverageStats(isNumbered);
                                        if (avgStats) {
                                            const avg_elite_percentile = avgStats.elite_count !== null ? calculatePercentile(avgStats.elite_count, colorEliteValues) : null;
                                            const avg_high_percentile = avgStats.high_count !== null ? calculatePercentile(avgStats.high_count, colorHighValues) : null;
                                            const upsetCountedEvents = displayedEvents
                                                .filter(e => e.event_type === (isNumbered ? 'numbered' : 'non-numbered'))
                                                .filter(e => upsetCountData[e.event_name] !== undefined && upsetCountData[e.event_name] !== null);
                                            const avg_upset_count = upsetCountedEvents.length > 0
                                                ? upsetCountedEvents.reduce((sum, e) => sum + upsetCountData[e.event_name], 0) / upsetCountedEvents.length
                                                : null;
                                            const upsetScoredEvents = displayedEvents
                                                .filter(e => e.event_type === (isNumbered ? 'numbered' : 'non-numbered'))
                                                .filter(e => upsetScoreData[e.event_name] !== undefined && upsetScoreData[e.event_name] !== null);
                                            // Geometric mean, consistent with how each event's score is computed
                                            const avg_upset_score = upsetScoredEvents.length > 0
                                                ? Math.exp(upsetScoredEvents.reduce((sum, e) => sum + Math.log(upsetScoreData[e.event_name]), 0) / upsetScoredEvents.length)
                                                : null;
                                            const avg_upset_score_percentile = avg_upset_score !== null && colorUpsetScoreValues.length > 0 ? calculatePercentile(avg_upset_score, colorUpsetScoreValues) : null;
                                            const mismatchEvents = displayedEvents
                                                .filter(e => e.event_type === (isNumbered ? 'numbered' : 'non-numbered'))
                                                .filter(e => mismatchData[e.event_name] !== undefined && mismatchData[e.event_name] !== null);
                                            // Geometric mean, consistent with how each event's value is computed
                                            const avg_mismatch = mismatchEvents.length > 0
                                                ? Math.exp(mismatchEvents.reduce((sum, e) => sum + Math.log(mismatchData[e.event_name]), 0) / mismatchEvents.length)
                                                : null;
                                            const avg_mismatch_percentile = avg_mismatch !== null && colorMismatchValues.length > 0 ? calculatePercentile(avg_mismatch, colorMismatchValues) : null;
                                            const recentFormEvents = displayedEvents
                                                .filter(e => e.event_type === (isNumbered ? 'numbered' : 'non-numbered'))
                                                .filter(e => recentFormData[e.event_name] !== undefined && recentFormData[e.event_name] !== null);
                                            const avg_recent_form = recentFormEvents.length > 0
                                                ? recentFormEvents.reduce((sum, e) => sum + recentFormData[e.event_name], 0) / recentFormEvents.length
                                                : null;
                                            const winStreakEvents = displayedEvents
                                                .filter(e => e.event_type === (isNumbered ? 'numbered' : 'non-numbered'))
                                                .filter(e => winStreakData[e.event_name] !== undefined && winStreakData[e.event_name] !== null);
                                            const avg_win_streak = winStreakEvents.length > 0
                                                ? winStreakEvents.reduce((sum, e) => sum + winStreakData[e.event_name], 0) / winStreakEvents.length
                                                : null;
                                            
                                            allRows.push(
                                                <tr key={isNumbered ? 'avg-numbered' : 'avg-non-numbered'} style={{...styles.tableRow, background: '#252525'}}>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}></td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}>
                                                        {isNumbered ? 'Average Numbered Event' : 'Average Non-Numbered Event'}
                                                        <span style={{ fontSize: '0.65em', marginLeft: '4px' }}>(in selected time period)</span>
                                                    </td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}></td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}>{avgStats.number_of_fights.toFixed(1)}</td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}><span style={{ color: '#888' }}>{Math.round(avgStats.finish_percentage * 100)}%</span></td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}>{avgStats.title_fight_count.toFixed(1)}</td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}><span style={{ color: '#888' }}>{avgStats.elite_count !== null ? avgStats.elite_count.toFixed(1) : ''}</span></td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}><span style={{ color: '#888' }}>{avgStats.high_count !== null ? avgStats.high_count.toFixed(1) : ''}</span></td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}><span style={{ color: '#888' }}>{avgStats.strength_score !== null ? avgStats.strength_score.toFixed(0) : ''}</span></td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}><span style={{ color: '#888' }}>{avg_upset_count !== null ? avg_upset_count.toFixed(1) : ''}</span></td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}><span style={{ color: getPercentileColor(avg_upset_score_percentile) }}>{avg_upset_score !== null ? avg_upset_score.toFixed(2) : ''}</span></td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}><span style={{ color: getPercentileColor(avg_mismatch_percentile) }}>{avg_mismatch !== null ? avg_mismatch.toFixed(2) : ''}</span></td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}><span style={{ color: '#888' }}>{avg_recent_form !== null ? Math.round(avg_recent_form * 100) + '%' : ''}</span></td>
                                                    <td style={{...styles.tableCell, fontStyle: 'italic', color: '#888'}}><span style={{ color: '#888' }}>{avg_win_streak !== null ? avg_win_streak.toFixed(2) : ''}</span></td>
                                                </tr>
                                            );
                                        }
                                    });
                                    
                                    // Add regular event rows
                                    allRows.push(...displayedEvents.map((event, idx) => {
                                        // Calculate percentiles for coloring (based on filtered events)
                                        const finishColorPercentile = event.finish_percentage !== undefined && !isNaN(event.finish_percentage) ? calculatePercentile(event.finish_percentage, colorFinishValues) : null;
                                        const eliteColorPercentile = eliteFightersData.percentages && eliteFightersData.percentages[event.event_name] !== undefined ? calculatePercentile(eliteFightersData.counts[event.event_name], colorEliteValues) : null;
                                        const highColorPercentile = highFightersData.percentages && highFightersData.percentages[event.event_name] !== undefined ? calculatePercentile(highFightersData.counts[event.event_name], colorHighValues) : null;
                                        const strengthScoreColorPercentile = event.strength_score !== null && event.strength_score !== undefined && !isNaN(event.strength_score) ? calculatePercentile(event.strength_score, colorStrengthScoreValues) : null;
                                        const upsetCountColorPercentile = upsetCountData[event.event_name] !== undefined && upsetCountData[event.event_name] !== null ? calculatePercentile(upsetCountData[event.event_name], colorUpsetCountValues) : null;
                                        const upsetScoreColorPercentile = upsetScoreData[event.event_name] !== undefined && upsetScoreData[event.event_name] !== null ? calculatePercentile(upsetScoreData[event.event_name], colorUpsetScoreValues) : null;
                                        const mismatchColorPercentile = mismatchData[event.event_name] !== undefined && mismatchData[event.event_name] !== null ? calculatePercentile(mismatchData[event.event_name], colorMismatchValues) : null;
                                        const recentFormColorPercentile = recentFormData[event.event_name] !== undefined && recentFormData[event.event_name] !== null ? calculatePercentile(recentFormData[event.event_name], colorRecentFormValues) : null;
                                        const winStreakColorPercentile = winStreakData[event.event_name] !== undefined && winStreakData[event.event_name] !== null ? calculatePercentile(winStreakData[event.event_name], colorWinStreakValues) : null;

                                        // Display raw strength score (not percentile)
                                        
                                        return (
                                    <>
                                    <tr key={idx} style={{...styles.tableRow, cursor: 'pointer'}} onClick={() => {
                                        if (expandedEvent === event.event_name) {
                                            setExpandedEvent(null);
                                        } else {
                                            setExpandedEvent(event.event_name);
                                            fetchEventFights(event.event_name);
                                        }
                                    }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333c4d'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                        <td style={{ ...styles.transparentCell, width: '1%', padding: '4px 4px', textAlign: 'right' }}>{idx + 1}</td>
                                        <td style={styles.eventNameCell}>
                                            {event.event_url ? (
                                                <a href={event.event_url} target="_blank" rel="noopener noreferrer" style={styles.link}>
                                                    {event.event_name}
                                                </a>
                                            ) : (
                                                event.event_name
                                            )}
                                        </td>
                                        <td style={styles.transparentCell}>{new Date(event.date).toLocaleDateString(undefined, { year: '2-digit', month: 'numeric', day: 'numeric' })}</td>
                                        <td style={styles.transparentCell}>{event.number_of_fights}</td>
                                        <td style={styles.tableCell}>{event.finish_percentage !== undefined && !isNaN(event.finish_percentage) ? <span style={{ color: getPercentileColor(finishColorPercentile) }}>{Math.round(event.finish_percentage * 100) + '%'}</span> : ''}</td>
                                        <td style={{ ...styles.tableCell, padding: '4px 6px' }}>{event.title_fight_count > 0 ? event.title_fight_count : ''}</td>
                                        <td style={{ ...styles.tableCell, padding: '4px 6px' }}>{eliteFightersData.percentages && eliteFightersData.percentages[event.event_name] !== undefined ? <span style={{ color: getPercentileColor(eliteColorPercentile) }}>{eliteFightersData.counts[event.event_name]}</span> : ''}</td>
                                        <td style={{ ...styles.tableCell, padding: '4px 6px' }}>{highFightersData.percentages && highFightersData.percentages[event.event_name] !== undefined ? <span style={{ color: getPercentileColor(highColorPercentile) }}>{highFightersData.counts[event.event_name]}</span> : ''}</td>
                                        <td style={styles.tableCell}>{event.strength_score !== null && event.strength_score !== undefined ? <span style={{ color: getPercentileColor(strengthScoreColorPercentile) }}>{event.strength_score.toFixed(0)}</span> : ''}</td>
                                        <td style={{ ...styles.tableCell, padding: '4px 6px' }}>{upsetCountData[event.event_name] !== undefined && upsetCountData[event.event_name] !== null ? <span style={{ color: getPercentileColor(upsetCountColorPercentile) }}>{upsetCountData[event.event_name]}</span> : ''}</td>
                                        <td style={styles.tableCell}>{upsetScoreData[event.event_name] !== undefined && upsetScoreData[event.event_name] !== null ? <span style={{ color: getPercentileColor(upsetScoreColorPercentile) }}>{upsetScoreData[event.event_name].toFixed(2)}</span> : ''}</td>
                                        <td style={styles.tableCell}>{mismatchData[event.event_name] !== undefined && mismatchData[event.event_name] !== null ? <span style={{ color: getPercentileColor(mismatchColorPercentile) }}>{mismatchData[event.event_name].toFixed(2)}</span> : ''}</td>
                                        <td style={styles.tableCell}>{recentFormData[event.event_name] !== undefined && recentFormData[event.event_name] !== null ? <span style={{ color: getPercentileColor(recentFormColorPercentile) }}>{Math.round(recentFormData[event.event_name] * 100) + '%'}</span> : ''}{renderL5Counts(recentFormCounts[event.event_name])}</td>
                                        <td style={{ ...styles.tableCell, padding: '4px 6px' }}>{winStreakData[event.event_name] !== undefined && winStreakData[event.event_name] !== null ? <span style={{ color: getPercentileColor(winStreakColorPercentile) }}>{winStreakData[event.event_name].toFixed(2)}</span> : ''}</td>
                                    </tr>
                                    {expandedEvent === event.event_name && (
                                        <tr key={`expanded-${idx}`} style={{ background: '#0f0f0f' }}>
                                            <td colSpan={14} style={{ padding: '12px 0 12px 12px', border: 'none', borderBottom: '1px solid #444' }}>
                                                {eventFightsLoading && !eventFightsCache[event.event_name] ? (
                                                    <div style={{ color: '#888', padding: '20px', textAlign: 'center' }}>Loading fight details...</div>
                                                ) : eventFightsCache[event.event_name] && eventFightsCache[event.event_name].length > 0 ? (
                                                    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                                        <thead>
                                                            <tr style={{ borderBottom: '1px solid #555' }}>
                                                                <th style={{ textAlign: 'left', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Fighter 1</th>
                                                                <th style={{ textAlign: 'left', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Fighter 2</th>
                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Rank 1</th>
                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Rank 2</th>
                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Class 1</th>
                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Class 2</th>
                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Rank Score</th>
                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}>Win. Odds</th>
                                                                <th style={{ textAlign: 'center', padding: '8px', color: '#aaa', fontWeight: 'normal' }}></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(() => {
                                                                const fights = eventFightsCache[event.event_name] || [];
                                                                return fights.map((fight, fIdx) => {
                                                                    const eliteStatus = getFightEliteStatus(
                                                                        fight.fighter1_rank,
                                                                        fight.fighter2_rank,
                                                                        fight.fighter1_weight_class,
                                                                        fight.fighter2_weight_class
                                                                    );
                                                                    const rankScorePercentile = fight.rank_score !== null
                                                                        ? calculatePercentile(fight.rank_score, allRankScores)
                                                                        : null;
                                                                    const upsetScorePercentile = fight.upset_score != null
                                                                        ? calculatePercentile(fight.upset_score, allFightUpsetScores)
                                                                        : null;
                                                                    return (
                                                                    <tr key={fIdx} style={{ borderBottom: '1px solid #333', background: fIdx % 2 === 0 ? 'transparent' : '#0a0a0a' }}>
                                                                        <td style={{ padding: '6px 8px', color: '#ddd' }}>
                                                                            {fight.fighter1}
                                                                        </td>
                                                                        <td style={{ padding: '6px 8px', color: '#ddd' }}>
                                                                            {fight.fighter2}
                                                                        </td>
                                                                        <td style={{ padding: '6px 8px', textAlign: 'center', color: '#ccc' }}>
                                                                            {fight.fighter1_rank ?? ''}
                                                                        </td>
                                                                        <td style={{ padding: '6px 8px', textAlign: 'center', color: '#ccc' }}>
                                                                            {fight.fighter2_rank ?? ''}
                                                                        </td>
                                                                        <td style={{ padding: '6px 8px', textAlign: 'center', color: '#ccc' }}>
                                                                            {fight.fighter1_weight_class ?? ''}
                                                                        </td>
                                                                        <td style={{ padding: '6px 8px', textAlign: 'center', color: '#ccc' }}>
                                                                            {fight.fighter2_weight_class ?? ''}
                                                                        </td>
                                                                        <td style={{ padding: '6px 8px', textAlign: 'center', color: rankScorePercentile !== null ? getPercentileColor(rankScorePercentile) : '#ccc' }}>
                                                                            {fight.rank_score !== null ? fight.rank_score.toFixed(1) : ''}
                                                                        </td>
                                                                    <td style={{ padding: '6px 8px', textAlign: 'center', color: upsetScorePercentile !== null ? getPercentileColor(upsetScorePercentile, 95) : '#ccc' }}>
                                                                        {fight.upset_score != null ? fight.upset_score.toFixed(2) : ''}
                                                                    </td>
                                                                    <td style={{ padding: '6px 8px', textAlign: 'center', color: eliteStatus === 'Elite' ? '#FFD700' : eliteStatus === 'High' ? '#FFA500' : '#888' }}>
                                                                        {eliteStatus === 'High' ? 'Sub-Elite' : eliteStatus}
                                                                    </td>
                                                                </tr>
                                                                    );
                                                                });
                                                            })()}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div style={{ color: '#888', padding: '20px', textAlign: 'center' }}>No fights found</div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    </>
                                        );
                                    }));
                                    
                                    return allRows;
                                })()}
                            </tbody>
                        </table>
                        </div>
                        
                        {/* Column header tooltip */}
                        {hoveredColumnHeader && (
                            <div style={{
                                position: 'fixed',
                                left: tooltipPos.x,
                                top: tooltipPos.y,
                                transform: 'translateX(-50%)',
                                background: '#2a2a2a',
                                border: '1px solid #555',
                                borderRadius: '4px',
                                padding: '6px 10px',
                                fontSize: '12px',
                                color: '#ddd',
                                zIndex: 1000,
                                whiteSpace: 'pre',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
                                pointerEvents: 'none'
                            }}>
                                {columnDescriptions[hoveredColumnHeader] || ''}
                            </div>
                        )}
                        {!showAllTableEvents && getSortedEvents().length > 20 && (
                            <div style={{ textAlign: 'center', marginTop: '15px' }}>
                                <button
                                    onClick={() => setShowAllTableEvents(true)}
                                    style={styles.seeMoreButton}
                                >
                                    See More ({getSortedEvents().length} total)
                                </button>
                            </div>
                        )}
                        {showAllTableEvents && getSortedEvents().length > 20 && (
                            <div style={{ textAlign: 'center', marginTop: '15px' }}>
                                <button
                                    onClick={() => setShowAllTableEvents(false)}
                                    style={styles.seeMoreButton}
                                >
                                    See Less
                                </button>
                            </div>
                        )}
                        <div style={styles.tableFootnote}>
                            <p style={styles.footnoteText}>
                                * Geometric mean of the decimal odds. 
                            </p>
                        </div>
                    </>
                )}
            </div>

            {/* Elite Fighters Configuration */}
            <div style={styles.eliteThresholdsContainer}>
                <h3 style={styles.eliteThresholdsTitle}>Elite Fighter Definition</h3>
                <p style={styles.eliteThresholdsSubtitle}>Set the maximum rank for each weight class to be considered "elite". A fight is elite if at least one fighter is ranked at or above the threshold.</p>
                <p style={styles.eliteThresholdsSubtitle}>Note: Be mindful of the consequences of using a fixed threshold across time, especially when it comes to the early pro days of each weight class.</p>
                <div style={styles.eliteThresholdsGrid}>
                    {Object.entries(eliteThresholds).map(([division, threshold]) => (
                        <div key={division} style={styles.eliteThresholdGroup}>
                            <label style={styles.eliteThresholdLabel}>{division}</label>
                            <input
                                type="number"
                                value={threshold}
                                onChange={(e) => setEliteThresholds({
                                    ...eliteThresholds,
                                    [division]: Math.max(1, parseInt(e.target.value) || 1)
                                })}
                                min="1"
                                max="100"
                                style={styles.eliteThresholdInput}
                            />
                        </div>
                    ))}
                </div>
                <div style={styles.eliteThresholdsButtonGroup}>
                    <button
                        onClick={() => {
                            setAppliedEliteThresholds(eliteThresholds);
                            fetchEliteFightersPercentage();
                        }}
                        style={styles.applyButton}
                    >
                        Apply
                    </button>
                    <button
                        onClick={() => {
                            const defaults = {
                                HW: 3, LHW: 3, MW: 5, WW: 9, LW: 10, FW: 9, BW: 8, FLW: 5,
                                W145: 2, W135: 2, W125: 2, W115: 2
                            };
                            setEliteThresholds(defaults);
                            setAppliedEliteThresholds(defaults);
                        }}
                        style={styles.clearButton}
                    >
                        Reset to Defaults
                    </button>
                </div>
            </div>

            <div style={styles.chartContainer}>
                <h1 style={styles.title}>UFC Events by Year</h1>
                <p style={styles.subtitle}>
                    Total Events: {data.reduce((sum, d) => sum + d.event_count, 0)} | 
                    Years: {data[0]?.year} - {data[data.length - 1]?.year}
                </p>
                <p style={styles.note}>
                    * Does not include "Road to UFC" or "Contender Series" events
                </p>
                
                <svg width={width} height={height} style={styles.svg}>
                    {/* Horizontal grid lines */}
                    <g>
                        {(() => {
                            const maxScale = 40;
                            const step = maxCount <= maxScale ? 10 : Math.ceil(maxCount / 4 / 10) * 10;
                            const ticks = [];
                            for (let i = step; i <= Math.max(maxScale, maxCount); i += step) {
                                ticks.push(i);
                            }
                            return ticks.map(value => {
                                const proportion = value / Math.max(maxScale, maxCount);
                                const yPos = padding.top + chartHeight - (proportion * chartHeight);
                                return (
                                    <g key={value}>
                                        <line
                                            x1={padding.left}
                                            y1={yPos}
                                            x2={width - padding.right}
                                            y2={yPos}
                                            stroke="#444"
                                            strokeWidth="1"
                                            strokeDasharray="4 4"
                                        />
                                        <text
                                            x={padding.left - 10}
                                            y={yPos + 5}
                                            textAnchor="end"
                                            fill="#ccc"
                                            fontSize="12"
                                        >
                                            {value}
                                        </text>
                                    </g>
                                );
                            });
                        })()}
                    </g>

                    {/* Era separator lines and labels */}
                    {(() => {
                        const era2005Index = data.findIndex(d => d.year === 2005);
                        const era2014Index = data.findIndex(d => d.year === 2014);
                        const lines = [];
                        
                        if (era2005Index !== -1) {
                            const x2005 = getX(era2005Index);
                            lines.push(
                                <line key="era2005-line" x1={x2005} y1={padding.top} x2={x2005} y2={height - padding.bottom} stroke="#666" strokeWidth="1" strokeDasharray="5 5" />
                            );
                        }
                        
                        if (era2014Index !== -1) {
                            const x2014 = getX(era2014Index);
                            lines.push(
                                <line key="era2014-line" x1={x2014} y1={padding.top} x2={x2014} y2={height - padding.bottom} stroke="#666" strokeWidth="1" strokeDasharray="5 5" />
                            );
                        }
                        
                        // Era labels
                        if (era2005Index !== -1 && era2014Index !== -1) {
                            const x2005 = getX(era2005Index);
                            const x2014 = getX(era2014Index);
                            const x0 = padding.left;
                            const middleY = padding.top + chartHeight / 3;
                            
                            // Beginnings
                            lines.push(
                                <text key="era-beginnings" x={(x0 + x2005) / 2} y={middleY} textAnchor="middle" fill="#999" fontSize="13" fontWeight="bold">
                                    Early Struggles
                                </text>
                            );
                            
                            // Expansion
                            lines.push(
                                <text key="era-expansion" x={(x2005 + x2014) / 2} y={middleY} textAnchor="middle" fill="#999" fontSize="13" fontWeight="bold">
                                    Expansion
                                </text>
                            );
                            
                            // Modern Era
                            lines.push(
                                <text key="era-modern" x={(x2014 + width - padding.right) / 2} y={middleY} textAnchor="middle" fill="#999" fontSize="13" fontWeight="bold">
                                    Modern Era
                                </text>
                            );
                        }
                        
                        return lines;
                    })()}

                    {/* Bars */}
                    {data.map((d, index) => {
                        const x = getX(index);
                        const y = getY(d.event_count);
                        const barWidth = getBarWidth();
                        const isHovered = hoveredBar === index;
                        const radius = 5;

                        return (
                            <g key={d.year}>
                                <circle
                                    cx={x + barWidth / 2}
                                    cy={y}
                                    r={radius}
                                    fill={isHovered ? '#64B5F6' : '#5C9EE8'}
                                    opacity={isHovered ? 1 : 0.85}
                                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                    onMouseEnter={() => setHoveredBar(index)}
                                    onMouseLeave={() => setHoveredBar(null)}
                                />
                                {/* Year label on X-axis */}
                                {(d.year % 5 === 0) && (
                                    <text
                                        x={x + barWidth / 2}
                                        y={height - padding.bottom + 20}
                                        textAnchor="middle"
                                        fill="#ccc"
                                        fontSize="11"
                                    >
                                        {d.year}
                                    </text>
                                )}
                                {/* Show count on hover */}
                                {isHovered && (
                                    <g>
                                        <rect
                                            x={x + barWidth / 2 - 30}
                                            y={y - 40}
                                            width={60}
                                            height={35}
                                            fill="#222"
                                            stroke="#555"
                                            strokeWidth="1"
                                            rx="5"
                                        />
                                        <text
                                            x={x + barWidth / 2}
                                            y={y - 25}
                                            textAnchor="middle"
                                            fill="#999"
                                            fontSize="11"
                                        >
                                            {d.year}
                                        </text>
                                        <text
                                            x={x + barWidth / 2}
                                            y={y - 10}
                                            textAnchor="middle"
                                            fill="#aaa"
                                            fontSize="10"
                                        >
                                            {d.event_count}
                                        </text>
                                    </g>
                                )}
                            </g>
                        );
                    })}

                    {/* X-axis line */}
                    <line
                        x1={padding.left}
                        y1={height - padding.bottom}
                        x2={width - padding.right}
                        y2={height - padding.bottom}
                        stroke="#666"
                        strokeWidth="2"
                    />

                    {/* Y-axis line */}
                    <line
                        x1={padding.left}
                        y1={padding.top}
                        x2={padding.left}
                        y2={height - padding.bottom}
                        stroke="#666"
                        strokeWidth="2"
                    />

                    {/* Y-axis label */}
                    <text
                        x={padding.left - 45}
                        y={padding.top + chartHeight / 2}
                        textAnchor="middle"
                        fill="#ccc"
                        fontSize="14"
                        fontWeight="bold"
                        transform={`rotate(-90, ${padding.left - 45}, ${padding.top + chartHeight / 2})`}
                    >
                        Number of Events
                    </text>

                    {/* X-axis label */}
                    <text
                        x={padding.left + chartWidth / 2}
                        y={height - 10}
                        textAnchor="middle"
                        fill="#ccc"
                        fontSize="14"
                        fontWeight="bold"
                    >
                        Year
                    </text>
                </svg>
            </div>

            {/* Cumulative Fights Chart */}
            <div style={styles.chartContainer}>
                <h1 style={styles.title}>Cumulative Number of Fights (UFC)</h1>
                {cumulativeLoading ? (
                    <div style={styles.loading}>Loading cumulative fights data...</div>
                ) : cumulativeError ? (
                    <div style={styles.error}>Error: {cumulativeError}</div>
                ) : !cumulativeData ? (
                    <div style={styles.error}>No cumulative fights data available</div>
                ) : (
                    <>
                        <div>
                            <label style={{ color: '#ddd', fontWeight: 500, display: 'block', marginBottom: '0.5em' }}>Split by Weight Class</label>
                            <div 
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.35rem 0.5rem',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    transition: 'all 0.25s ease'
                                }}
                                onClick={() => setShowDivisionsSeparate(!showDivisionsSeparate)}
                            >
                                <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '30px', textAlign: 'center' }}>Yes</span>
                                <div style={{
                                    width: '32px',
                                    height: '20px',
                                    background: '#444',
                                    borderRadius: '10px',
                                    position: 'relative',
                                    transition: 'all 0.25s ease',
                                    border: '1px solid #555'
                                }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        background: '#fff',
                                        borderRadius: '50%',
                                        position: 'absolute',
                                        top: '2px',
                                        left: showDivisionsSeparate ? '2px' : '14px',
                                        transition: 'all 0.25s ease'
                                    }} />
                                </div>
                                <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '30px', textAlign: 'center' }}>No</span>
                            </div>
                        </div>

                        <div style={styles.chartWithLegendContainer}>
                            <svg width={1100} height={500} style={styles.svg}>
                            {/* Vertical grid lines */}
                            <g>
                                {(() => {
                                    const chartHeight = 500 - 100;
                                    const padding = { top: 40, bottom: 60, left: 60, right: 200 };
                                    const chartWidth = 1100 - padding.left - padding.right;
                                    
                                    return cumulativeData.years
                                        .filter((year, idx) => (idx - 1) % Math.max(1, Math.floor(cumulativeData.years.length / 10)) === 0)
                                        .map((year) => {
                                            const yearIndex = cumulativeData.years.indexOf(year);
                                            const x = padding.left + (yearIndex / (cumulativeData.years.length - 1)) * chartWidth;
                                            return (
                                                <line
                                                    key={`vgrid-${year}`}
                                                    x1={x}
                                                    y1={padding.top}
                                                    x2={x}
                                                    y2={440}
                                                    stroke="#333"
                                                    strokeWidth="1"
                                                    opacity="0.5"
                                                />
                                            );
                                        });
                                })()}
                            </g>

                            {/* Horizontal grid lines */}
                            <g>
                                {(() => {
                                    const divisionsToShow = showDivisionsSeparate ? cumulativeData.divisions : ['All Divisions'];
                                    const maxCumulative = Math.max(...divisionsToShow.flatMap(d => {
                                        const divData = cumulativeData.data[d];
                                        return divData ? divData.map(p => p.cumulative) : [];
                                    }));
                                    
                                    // Calculate nice round step size
                                    const getStep = (max) => {
                                        const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
                                        const normalized = max / magnitude;
                                        let step;
                                        if (normalized <= 1.5) step = 0.2 * magnitude;
                                        else if (normalized <= 3) step = 0.5 * magnitude;
                                        else if (normalized <= 7) step = 1 * magnitude;
                                        else step = 2 * magnitude;
                                        return step;
                                    };
                                    
                                    const step = getStep(maxCumulative);
                                    const ticks = [];
                                    for (let i = step; i <= maxCumulative; i += step) {
                                        ticks.push(i);
                                    }
                                    
                                    const chartHeight = 500 - 100;
                                    const padding = { top: 40, bottom: 60, left: 60, right: 40 };
                                    
                                    return ticks.map(value => {
                                        const proportion = value / maxCumulative;
                                        const yPos = padding.top + chartHeight - (proportion * chartHeight);
                                        return (
                                            <g key={value}>
                                                <line
                                                    x1={padding.left}
                                                    y1={yPos}
                                                    x2={width - padding.right}
                                                    y2={yPos}
                                                    stroke="#444"
                                                    strokeWidth="1"
                                                    strokeDasharray="4 4"
                                                />
                                                <text
                                                    x={padding.left - 10}
                                                    y={yPos + 5}
                                                    textAnchor="end"
                                                    fill="#ccc"
                                                    fontSize="12"
                                                >
                                                    {value.toLocaleString()}
                                                </text>
                                            </g>
                                        );
                                    });
                                })()}
                            </g>

                            {/* Lines for each selected division */}
                            {(() => {
                                const chartHeight = 500 - 100;
                                const padding = { top: 40, bottom: 60, left: 60, right: 200 };
                                const chartWidth = 1100 - padding.left - padding.right;
                                
                                const divisionsToShow = showDivisionsSeparate ? cumulativeData.divisions : ['All Divisions'];
                                const allData = divisionsToShow.flatMap(d => cumulativeData.data[d] || []);
                                const maxCumulative = Math.max(...allData.map(p => p.cumulative), 1);
                                
                                // Weight class order for consistent coloring across spectrum
                                const weightClassOrder = ['HW', 'LHW', 'MW', 'WW', 'LW', 'FW', 'BW', 'FLW', 'W145', 'W135', 'W125', 'W115'];
                                
                                // Generate hues across the visible light spectrum based on weight class position
                                const getHueForDivision = (division) => {
                                    if (division === 'All Divisions') return 0;
                                    const index = weightClassOrder.indexOf(division);
                                    if (index === -1) return 0;
                                    if (weightClassOrder.length === 1) return 0;
                                    return (index / (weightClassOrder.length - 1)) * 270;
                                };
                                
                                const getColorFromHue = (hue) => {
                                    const saturation = 65;
                                    const lightness = 45;
                                    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
                                };
                                
                                return divisionsToShow.map((division, divIndex) => {
                                    const divData = cumulativeData.data[division];
                                    if (!divData) return null;
                                    
                                    const hue = getHueForDivision(division);
                                    const color = getColorFromHue(hue);
                                    const isHovered = hoveredPoint === division;
                                    
                                    // Determine opacity based on hover state
                                    const isAnyHovered = hoveredPoint !== null;
                                    const opacity = isAnyHovered ? (isHovered ? 1 : 0.2) : 1;
                                    
                                    // Create path for line
                                    let pathD = '';
                                    let lastX, lastY;
                                    divData.forEach((point, idx) => {
                                        const yearIndex = cumulativeData.years.indexOf(point.year);
                                        const x = padding.left + (yearIndex / (cumulativeData.years.length - 1)) * chartWidth;
                                        const y = padding.top + chartHeight - (point.cumulative / maxCumulative) * chartHeight;
                                        pathD += `${idx === 0 ? 'M' : 'L'} ${x} ${y} `;
                                        lastX = x;
                                        lastY = y;
                                    });
                                    
                                    return (
                                        <g key={`line-${division}`}
                                            onMouseEnter={() => setHoveredPoint(division)}
                                            onMouseLeave={() => setHoveredPoint(null)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <path
                                                d={pathD}
                                                stroke={color}
                                                strokeWidth={isHovered ? "3" : "2"}
                                                fill="none"
                                                opacity={opacity}
                                            />
                                            
                                            {/* Label at end of line */}
                                            <line
                                                x1={lastX}
                                                y1={lastY}
                                                x2={padding.left + chartWidth + 10}
                                                y2={lastY}
                                                stroke="#666"
                                                strokeWidth="1"
                                                strokeDasharray="2 2"
                                                opacity={opacity}
                                            />
                                            <text
                                                x={padding.left + chartWidth + 15}
                                                y={lastY + 4}
                                                fill={color}
                                                fontSize="12"
                                                fontWeight="400"
                                                opacity={opacity}
                                                style={{ 
                                                    cursor: 'pointer',
                                                    transition: 'opacity 0.2s ease'
                                                }}
                                                onMouseEnter={() => setHoveredPoint(division)}
                                                onMouseLeave={() => setHoveredPoint(null)}
                                            >
                                                {division}
                                            </text>
                                        </g>
                                    );
                                });
                            })()}

                            {/* X-axis line */}
                            <line
                                x1={60}
                                y1={440}
                                x2={1100 - 200}
                                y2={440}
                                stroke="#666"
                                strokeWidth="2"
                            />

                            {/* Y-axis line */}
                            <line
                                x1={60}
                                y1={40}
                                x2={60}
                                y2={440}
                                stroke="#666"
                                strokeWidth="2"
                            />

                            {/* X-axis labels (every 5 years) */}
                            {(() => {
                                const chartWidth = 1100 - 60 - 200;
                                return cumulativeData.years
                                    .filter((year, idx) => (idx - 1) % Math.max(1, Math.floor(cumulativeData.years.length / 10)) === 0)
                                    .map((year, idx) => {
                                        const yearIndex = cumulativeData.years.indexOf(year);
                                        const x = 60 + (yearIndex / (cumulativeData.years.length - 1)) * chartWidth;
                                        return (
                                            <text
                                                key={`year-${year}`}
                                                x={x}
                                                y={460}
                                                textAnchor="middle"
                                                fill="#ccc"
                                                fontSize="11"
                                            >
                                                {year}
                                            </text>
                                        );
                                    });
                            })()}

                            {/* Y-axis label */}
                            <text
                                x={15}
                                y={240}
                                textAnchor="middle"
                                fill="#ccc"
                                fontSize="14"
                                fontWeight="bold"
                                transform="rotate(-90, 15, 240)"
                            >
                                Cumulative Fights
                            </text>

                            {/* X-axis label */}
                            <text
                                x={550}
                                y={490}
                                textAnchor="middle"
                                fill="#ccc"
                                fontSize="14"
                                fontWeight="bold"
                            >
                                Year
                            </text>
                        </svg>
                        </div>
                    </>
                )}
            </div>

            {/* Scatter Plot: Fighter Ranks Score vs Finish Rate */}
            <div style={styles.chartContainer}>
                <h1 style={styles.title}>UFC Events: Ranks Score vs Finish Rate</h1>
                <div style={styles.filterContainer}>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Min Year:</label>
                        <input
                            type="number"
                            value={scatterMinYear}
                            onChange={(e) => setScatterMinYear(e.target.value)}
                            placeholder=""
                            style={styles.filterInput}
                        />
                    </div>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>Max Year:</label>
                        <input
                            type="number"
                            value={scatterMaxYear}
                            onChange={(e) => setScatterMaxYear(e.target.value)}
                            placeholder=""
                            style={styles.filterInput}
                        />
                    </div>
                    <button
                        onClick={() => {
                            setAppliedScatterMinYear(scatterMinYear);
                            setAppliedScatterMaxYear(scatterMaxYear);
                        }}
                        style={styles.applyButton}
                    >
                        Apply
                    </button>
                    {(appliedScatterMinYear || appliedScatterMaxYear) && (
                        <button
                            onClick={() => {
                                setScatterMinYear('');
                                setScatterMaxYear('');
                                setAppliedScatterMinYear('');
                                setAppliedScatterMaxYear('');
                            }}
                            style={styles.clearButton}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
                {getScatterPlotEvents().length > 0 ? (
                    (() => {
                        const sortedEvents = getScatterPlotEvents();
                        
                        // Get Fighter Ranks Score values for X-axis
                        const xValues = sortedEvents
                            .map(e => e.strength_score ?? 0)
                            .filter(v => v !== null && !isNaN(v));
                        
                        // Get Finish Rate values for Y-axis
                        const yValues = sortedEvents.map(e => e.finish_percentage ?? 0);
                        
                        const maxX = Math.max(...xValues, 0.1);
                        const maxY = Math.max(...yValues, 0.1);
                        
                        const scatterWidth = 900;
                        const scatterHeight = 600;
                        const scatterPadding = { top: 40, right: 60, bottom: 80, left: 80 };
                        const chartWidth = scatterWidth - scatterPadding.left - scatterPadding.right;
                        const chartHeight = scatterHeight - scatterPadding.top - scatterPadding.bottom;
                        
                        const getScatterX = (value) => scatterPadding.left + (value / maxX) * chartWidth;
                        const getScatterY = (value) => scatterPadding.top + chartHeight - (value / maxY) * chartHeight;
                        
                        // Calculate nice round steps for grid
                        const getStep = (max) => {
                            const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
                            const normalized = max / magnitude;
                            let step;
                            if (normalized <= 1.5) step = 0.2 * magnitude;
                            else if (normalized <= 3) step = 0.5 * magnitude;
                            else if (normalized <= 7) step = magnitude;
                            else step = 2 * magnitude;
                            return step;
                        };
                        
                        const xStep = getStep(maxX);
                        const yStep = getStep(maxY);
                        
                        const xTicks = [];
                        for (let i = xStep; i <= maxX; i += xStep) {
                            xTicks.push(i);
                        }
                        
                        const yTicks = [];
                        // For finish rates (0-1 range), use fixed 0.1 intervals for 0%, 10%, 20%, ..., 100%
                        if (maxY < 2) {
                            for (let i = 0; i <= 1; i += 0.1) {
                                yTicks.push(i);
                            }
                        } else {
                            for (let i = yStep; i <= maxY; i += yStep) {
                                yTicks.push(i);
                            }
                        }
                        
                        return (
                            <svg width={scatterWidth} height={scatterHeight} style={styles.svg}>
                                {/* Vertical grid lines */}
                                <g>
                                    {xTicks.map(value => {
                                        const x = getScatterX(value);
                                        return (
                                            <line
                                                key={`vgrid-${value}`}
                                                x1={x}
                                                y1={scatterPadding.top}
                                                x2={x}
                                                y2={scatterHeight - scatterPadding.bottom}
                                                stroke="#333"
                                                strokeWidth="1"
                                                opacity="0.5"
                                            />
                                        );
                                    })}
                                </g>

                                {/* Horizontal grid lines */}
                                <g>
                                    {yTicks.map(value => {
                                        const y = getScatterY(value);
                                        // If maxY < 2, values are decimals (0-1), so multiply by 100 for display
                                        const displayValue = maxY < 2 ? (value * 100) : value;
                                        return (
                                            <g key={`hgrid-${value}`}>
                                                <line
                                                    x1={scatterPadding.left}
                                                    y1={y}
                                                    x2={scatterWidth - scatterPadding.right}
                                                    y2={y}
                                                    stroke="#333"
                                                    strokeWidth="1"
                                                    opacity="0.5"
                                                />
                                                <text
                                                    x={scatterPadding.left - 20}
                                                    y={y + 5}
                                                    textAnchor="end"
                                                    fill="#ccc"
                                                    fontSize="12"
                                                >
                                                    {displayValue.toFixed(0)}%
                                                </text>
                                            </g>
                                        );
                                    })}
                                </g>

                                {/* Data points */}
                                {(() => {
                                    // Calculate date range for color gradient
                                    const dates = sortedEvents.map(e => new Date(e.date).getTime()).filter(d => !isNaN(d));
                                    const minDate = dates.length > 0 ? Math.min(...dates) : 0;
                                    const maxDate = dates.length > 0 ? Math.max(...dates) : 1;
                                    const dateRange = maxDate - minDate || 1;
                                    
                                    // Generate blue-to-white gradient based on date (inverted)
                                    const getColorFromDate = (eventDate) => {
                                        const dateMs = new Date(eventDate).getTime();
                                        const progress = (dateMs - minDate) / dateRange;
                                        // Progress from blue to white: blue (100% sat, 55% light) at 0% to white (0% sat, 100% light) at 100%
                                        const saturation = (1 - progress) * 100;
                                        const lightness = 55 + progress * 45;
                                        return `hsl(210, ${saturation}%, ${lightness}%)`;
                                    };
                                    
                                    return sortedEvents.map((event, idx) => {
                                        const strengthScore = event.strength_score ?? 0;
                                        const finishRate = event.finish_percentage ?? 0;
                                        const x = getScatterX(strengthScore);
                                        const y = getScatterY(finishRate);
                                        const circleColor = getColorFromDate(event.date);
                                        
                                        return (
                                            <g key={`point-${idx}`}>
                                                {/* Invisible larger hit zone for easy hovering */}
                                                <circle
                                                    cx={x}
                                                    cy={y}
                                                    r={12}
                                                    fill="transparent"
                                                    style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                                                    onMouseEnter={() => {
                                                        setHoveredScatterPoint(idx);
                                                        setScatterTooltipPos({ x, y });
                                                    }}
                                                    onMouseLeave={() => {
                                                        setHoveredScatterPoint(null);
                                                    }}
                                                />
                                                {/* Visible circle */}
                                                <circle
                                                    cx={x}
                                                    cy={y}
                                                    r={hoveredScatterPoint === idx ? 4 : 2.5}
                                                    fill={circleColor}
                                                    opacity={hoveredScatterPoint === idx ? 1 : 0.7}
                                                    style={{ transition: 'all 0.2s', pointerEvents: 'none' }}
                                                />
                                            </g>
                                        );
                                    });
                                })()}
                                
                                {/* Custom tooltip */}
                                {hoveredScatterPoint !== null && (() => {
                                    const tooltipOffsetRight = 45;
                                    const tooltipX = scatterTooltipPos.x + tooltipOffsetRight;
                                    // If tooltip would go off-screen on the right, position it on the left
                                    const adjustedX = tooltipX > (scatterWidth - scatterPadding.right - 50) 
                                        ? scatterTooltipPos.x - tooltipOffsetRight 
                                        : tooltipX;
                                    return (
                                        <g>
                                            <text
                                                x={adjustedX}
                                                y={scatterTooltipPos.y - 15}
                                                textAnchor="middle"
                                                fill="#aaa"
                                                fontSize="12"
                                            >
                                                {sortedEvents[hoveredScatterPoint]?.event_name}
                                            </text>
                                        </g>
                                    );
                                })()}

                                {/* X-axis line */}
                                <line
                                    x1={scatterPadding.left}
                                    y1={scatterHeight - scatterPadding.bottom}
                                    x2={scatterWidth - scatterPadding.right}
                                    y2={scatterHeight - scatterPadding.bottom}
                                    stroke="#666"
                                    strokeWidth="2"
                                />

                                {/* Y-axis line */}
                                <line
                                    x1={scatterPadding.left}
                                    y1={scatterPadding.top}
                                    x2={scatterPadding.left}
                                    y2={scatterHeight - scatterPadding.bottom}
                                    stroke="#666"
                                    strokeWidth="2"
                                />

                                {/* X-axis labels */}
                                {xTicks.map(value => {
                                    const x = getScatterX(value);
                                    return (
                                        <text
                                            key={`xlabel-${value}`}
                                            x={x}
                                            y={scatterHeight - scatterPadding.bottom + 25}
                                            textAnchor="middle"
                                            fill="#ccc"
                                            fontSize="12"
                                        >
                                            {value.toFixed(1)}
                                        </text>
                                    );
                                })}

                                {/* X-axis label */}
                                <text
                                    x={scatterPadding.left + chartWidth / 2}
                                    y={scatterHeight - 20}
                                    textAnchor="middle"
                                    fill="#ccc"
                                    fontSize="14"
                                    fontWeight="bold"
                                >
                                    Fighter Ranks Score
                                </text>

                                {/* Y-axis label */}
                                <text
                                    x={20}
                                    y={scatterPadding.top + chartHeight / 2}
                                    textAnchor="middle"
                                    fill="#ccc"
                                    fontSize="14"
                                    fontWeight="bold"
                                    transform={`rotate(-90, 20, ${scatterPadding.top + chartHeight / 2})`}
                                >
                                    Finish Rate (%)
                                </text>
                            </svg>
                        );
                    })()
                ) : (
                    <div style={styles.loading}>No data available</div>
                )}
            </div>

            {/* Rolling 1-Year Elite Fights Count (quarterly steps) */}
            <div style={styles.chartContainer}>
                <h1 style={styles.title}>
                    Rolling 1-Year {eliteDisplayMode === 'count' ? 'Count of Elite Fights' : eliteDisplayMode === 'fightRatio' ? 'Elite Fight Ratio' : `Elite Fights per Event${eliteNumberedOnly ? ' (Numbered Events)' : ''}`}
                </h1>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2rem', flexWrap: 'wrap' }}>
                    <div>
                        <label style={{ color: '#ddd', fontWeight: 500, display: 'block', marginBottom: '0.5em' }}>Display</label>
                        <div style={{ display: 'inline-flex', border: '1px solid #555', borderRadius: '6px', overflow: 'hidden' }}>
                            {[
                                { value: 'count', label: 'Count' },
                                { value: 'fightRatio', label: 'Ratio per Fight' },
                                { value: 'eventCount', label: 'Count per Event' }
                            ].map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => setEliteDisplayMode(option.value)}
                                    style={{
                                        padding: '0.4rem 0.8rem',
                                        fontSize: '0.9rem',
                                        fontWeight: 500,
                                        border: 'none',
                                        cursor: 'pointer',
                                        background: eliteDisplayMode === option.value ? '#64B5F6' : '#333',
                                        color: eliteDisplayMode === option.value ? '#111' : '#ddd',
                                        transition: 'all 0.25s ease'
                                    }}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {eliteDisplayMode === 'eventCount' && (
                        <div>
                            <label style={{ color: '#ddd', fontWeight: 500, display: 'block', marginBottom: '0.5em' }}>Events</label>
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.35rem 0.5rem',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    transition: 'all 0.25s ease'
                                }}
                                onClick={() => setEliteNumberedOnly(!eliteNumberedOnly)}
                            >
                                <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, textAlign: 'center' }}>All events</span>
                                <div style={{
                                    width: '32px',
                                    height: '20px',
                                    background: '#444',
                                    borderRadius: '10px',
                                    position: 'relative',
                                    transition: 'all 0.25s ease',
                                    border: '1px solid #555'
                                }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        background: '#fff',
                                        borderRadius: '50%',
                                        position: 'absolute',
                                        top: '2px',
                                        left: eliteNumberedOnly ? '14px' : '2px',
                                        transition: 'all 0.25s ease'
                                    }} />
                                </div>
                                <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, textAlign: 'center' }}>Numbered events only</span>
                            </div>
                        </div>
                    )}
                </div>
                {rollingEliteData.length > 0 ? (
                    (() => {
                        const getRollingValue = (d) => {
                            if (eliteDisplayMode === 'fightRatio') return d.eliteRatio * 100;
                            if (eliteDisplayMode === 'eventCount') {
                                return eliteNumberedOnly ? d.numberedEventEliteAvg : d.eventEliteAvg;
                            }
                            return d.eliteCount;
                        };
                        const isPercentMode = eliteDisplayMode === 'fightRatio';

                        // Skip windows with no events to average over
                        let filteredData = rollingEliteData.filter(d => getRollingValue(d) !== null);

                        if (filteredData.length === 0) {
                            return <div style={styles.loading}>No data available for selected date range</div>;
                        }

                        const rollingWidth = 1100;
                        const rollingHeight = 450;
                        const rollingPadding = { top: 40, right: 40, bottom: 80, left: 80 };
                        const rollingChartWidth = rollingWidth - rollingPadding.left - rollingPadding.right;
                        const rollingChartHeight = rollingHeight - rollingPadding.top - rollingPadding.bottom;

                        const maxRollingCount = Math.max(...filteredData.map(getRollingValue), 1);
                        const minDate = filteredData[0].date;
                        const maxDate = filteredData[filteredData.length - 1].date;
                        const dateRange = maxDate - minDate || 1;

                        const getRollingX = (dateObj) => {
                            return rollingPadding.left + ((dateObj - minDate) / dateRange) * rollingChartWidth;
                        };

                        const getRollingY = (count) => {
                            return rollingPadding.top + rollingChartHeight - (count / maxRollingCount) * rollingChartHeight;
                        };

                        // Calculate nice round steps for Y-axis
                        const getStep = (max) => {
                            const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
                            const normalized = max / magnitude;
                            let step;
                            if (normalized < 1.5) step = magnitude * 0.5;
                            else if (normalized < 3) step = magnitude;
                            else if (normalized < 7) step = magnitude * 2;
                            else step = magnitude * 5;
                            return step;
                        };

                        const yStep = getStep(maxRollingCount);
                        const yTicks = [];
                        for (let i = yStep; i <= maxRollingCount; i += yStep) {
                            yTicks.push(i);
                        }

                        return (
                            <>
                                <svg width={rollingWidth} height={rollingHeight} style={styles.svg}>
                                    {/* Horizontal grid lines */}
                                    <g>
                                        {yTicks.map(value => {
                                            const yPos = getRollingY(value);
                                            return (
                                                <g key={`hgrid-${value}`}>
                                                    <line
                                                        x1={rollingPadding.left}
                                                        y1={yPos}
                                                        x2={rollingWidth - rollingPadding.right}
                                                        y2={yPos}
                                                        stroke="#333"
                                                        strokeWidth="1"
                                                        strokeDasharray="4 4"
                                                        opacity="0.8"
                                                    />
                                                    <text
                                                        x={rollingPadding.left - 10}
                                                        y={yPos + 5}
                                                        textAnchor="end"
                                                        fill="#999"
                                                        fontSize="12"
                                                    >
                                                        {isPercentMode ? `${+value.toFixed(2)}%` : +value.toFixed(2)}
                                                    </text>
                                                </g>
                                            );
                                        })}
                                    </g>

                                    {/* Line chart */}
                                    <path
                                        d={filteredData
                                            .map((d, idx) => `${idx === 0 ? 'M' : 'L'} ${getRollingX(d.date)} ${getRollingY(getRollingValue(d))}`)
                                            .join(' ')}
                                        fill="none"
                                        stroke="#5A9FE0"
                                        strokeWidth="2"
                                        strokeLinejoin="round"
                                        strokeLinecap="round"
                                        style={{ pointerEvents: 'none' }}
                                    />

                                    {/* Data points */}
                                    {filteredData.map((d, idx) => {
                                        const x = getRollingX(d.date);
                                        const y = getRollingY(getRollingValue(d));
                                        const isHovered = hoveredRollingPoint === idx;

                                        return (
                                            <g key={idx}>
                                                {/* Invisible larger hit area for better hover detection */}
                                                <circle
                                                    cx={x}
                                                    cy={y}
                                                    r={12}
                                                    fill="transparent"
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseEnter={() => setHoveredRollingPoint(idx)}
                                                    onMouseLeave={() => setHoveredRollingPoint(null)}
                                                />
                                                {/* Visible circle */}
                                                <circle
                                                    cx={x}
                                                    cy={y}
                                                    r={isHovered ? 5 : 0}
                                                    fill="#64B5F6"
                                                    style={{ pointerEvents: 'none', transition: 'all 0.2s' }}
                                                />
                                                {isHovered && (
                                                    <g style={{ pointerEvents: 'none' }}>
                                                        <rect
                                                            x={x + 15}
                                                            y={y - 75}
                                                            width={150}
                                                            height={65}
                                                            fill="#222"
                                                            stroke="#555"
                                                            strokeWidth="1"
                                                            rx="5"
                                                        />
                                                        <text
                                                            x={x + 90}
                                                            y={y - 55}
                                                            textAnchor="middle"
                                                            fill="#999"
                                                            fontSize="12"
                                                        >
                                                            {`${d.windowStart.toLocaleString('en-US', { month: 'short', year: 'numeric' })} – ${new Date(d.date).toLocaleString('en-US', { month: 'short', year: 'numeric' })}`}
                                                        </text>
                                                        <text
                                                            x={x + 90}
                                                            y={y - 35}
                                                            textAnchor="middle"
                                                            fill="#aaa"
                                                            fontSize="11"
                                                        >
                                                            {eliteDisplayMode === 'count' ? 'Elite Fights:' : eliteDisplayMode === 'fightRatio' ? 'Elite Fight Ratio:' : 'Elite Fights per Event:'}
                                                        </text>
                                                        <text
                                                            x={x + 90}
                                                            y={y - 17}
                                                            textAnchor="middle"
                                                            fill="#aaa"
                                                            fontSize="11"
                                                        >
                                                            {eliteDisplayMode === 'count' ? d.eliteCount : eliteDisplayMode === 'fightRatio' ? `${(d.eliteRatio * 100).toFixed(1)}% (${d.eliteCount}/${d.totalFights})` : `${getRollingValue(d).toFixed(2)} (${eliteNumberedOnly ? d.numberedEventCount : d.eventCount} events)`}
                                                        </text>
                                                    </g>
                                                )}
                                            </g>
                                        );
                                    })}

                                    {/* X-axis */}
                                    <line
                                        x1={rollingPadding.left}
                                        y1={rollingHeight - rollingPadding.bottom}
                                        x2={rollingWidth - rollingPadding.right}
                                        y2={rollingHeight - rollingPadding.bottom}
                                        stroke="#666"
                                        strokeWidth="2"
                                    />

                                    {/* Y-axis */}
                                    <line
                                        x1={rollingPadding.left}
                                        y1={rollingPadding.top}
                                        x2={rollingPadding.left}
                                        y2={rollingHeight - rollingPadding.bottom}
                                        stroke="#666"
                                        strokeWidth="2"
                                    />

                                    {/* Vertical dashed lines for 5-year intervals */}
                                    {(() => {
                                        const minYear = minDate.getFullYear();
                                        const maxYear = maxDate.getFullYear();
                                        const lines = [];
                                        
                                        // Start from nearest 5-year interval
                                        const startYear = Math.ceil(minYear / 5) * 5;
                                        
                                        for (let year = startYear; year <= maxYear; year += 5) {
                                            // Create a date for January 1st of that year
                                            const yearDate = new Date(year, 0, 1);
                                            const x = getRollingX(yearDate);
                                            
                                            lines.push(
                                                <line
                                                    key={`vline-${year}`}
                                                    x1={x}
                                                    y1={rollingPadding.top}
                                                    x2={x}
                                                    y2={rollingHeight - rollingPadding.bottom}
                                                    stroke="#444"
                                                    strokeWidth="1"
                                                    strokeDasharray="4 4"
                                                    opacity="0.7"
                                                />
                                            );
                                        }
                                        return lines;
                                    })()}

                                    {/* X-axis labels (5-year intervals: 1995, 2000, 2005, etc.) */}
                                    {(() => {
                                        const minYear = minDate.getFullYear();
                                        const maxYear = maxDate.getFullYear();
                                        const labels = [];
                                        
                                        // Start from nearest 5-year interval
                                        const startYear = Math.ceil(minYear / 5) * 5;
                                        
                                        for (let year = startYear; year <= maxYear; year += 5) {
                                            // Create a date for January 1st of that year
                                            const yearDate = new Date(year, 0, 1);
                                            const x = getRollingX(yearDate);
                                            
                                            labels.push(
                                                <text key={`xlabel-${year}`} x={x} y={rollingHeight - rollingPadding.bottom + 15} textAnchor="middle" fill="#999" fontSize="12">
                                                    {year}
                                                </text>
                                            );
                                        }
                                        return labels;
                                    })()}

                                    {/* Y-axis label */}
                                    <text
                                        x={20}
                                        y={rollingPadding.top + rollingChartHeight / 2}
                                        textAnchor="middle"
                                        fill="#ccc"
                                        fontSize="14"
                                        fontWeight="bold"
                                        transform={`rotate(-90, 20, ${rollingPadding.top + rollingChartHeight / 2})`}
                                    >
                                        {eliteDisplayMode === 'count' ? 'Elite Fights (Trailing 12 Months)' : eliteDisplayMode === 'fightRatio' ? 'Elite Fight Ratio (%, Trailing 12 Months)' : 'Elite Fights per Event (Trailing 12 Months)'}
                                    </text>
                                </svg>
                            </>
                        );
                    })()
                ) : (
                    <div style={styles.loading}>Loading elite fights data...</div>
                )}
            </div>

            {/* Title Fights by Year and Division Table */}
            <div style={styles.tableContainer}>
                <h2 style={styles.tableTitle}>Title Fights by Year and Weight Class</h2>
                <label
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: '#fff',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        marginBottom: '1rem'
                    }}
                >
                    <input
                        type="checkbox"
                        checked={undisputedTitleFightsOnly}
                        onChange={(e) => setUndisputedTitleFightsOnly(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                    />
                    Undisputed only
                </label>

                {titleFightsByYearLoading ? (
                    <div style={styles.loading}>Loading title fights data...</div>
                ) : titleFightsByYearError ? (
                    <div style={styles.error}>Error: {titleFightsByYearError}</div>
                ) : titleFightsByYearAndDivision ? (
                    <div style={styles.tableWrapper}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.tableHeader}>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell }}>Year</th>
                                    {titleFightsByYearAndDivision.weight_classes.map(wc => (
                                        <th key={wc} style={{ ...styles.tableCell, ...styles.headerCell, textAlign: 'center' }}>
                                            {wc}
                                        </th>
                                    ))}
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, textAlign: 'center' }}>Total</th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, textAlign: 'center' }}>Active WCs</th>
                                    <th style={{ ...styles.tableCell, ...styles.headerCell, textAlign: 'center' }}>Per Active WC</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    // Color per-active-WC ratios with the cells' color scheme (red -> orange ->
                                    // light green -> green), stretched over the column's min-max range
                                    const yearlyRatios = titleFightsByYearAndDivision.data
                                        .filter(yearData => (yearData.active_weight_classes || []).length > 0)
                                        .map(yearData => Object.values(yearData.divisions).reduce((sum, count) => sum + count, 0) / yearData.active_weight_classes.length);
                                    const minRatio = Math.min(...yearlyRatios);
                                    const maxRatio = Math.max(...yearlyRatios);
                                    const getTitleFightRatioColor = (ratio) => {
                                        const stops = [[229, 57, 53], [255, 167, 38], [129, 199, 132], [76, 175, 80]];
                                        const position = maxRatio > minRatio
                                            ? Math.min(Math.max((ratio - minRatio) / (maxRatio - minRatio), 0), 1) * (stops.length - 1)
                                            : (stops.length - 1) / 2;
                                        const i = Math.min(Math.floor(position), stops.length - 2);
                                        const t = position - i;
                                        return `rgb(${stops[i].map((c, k) => Math.round(c + (stops[i + 1][k] - c) * t)).join(',')})`;
                                    };

                                    // Calculate totals and active years for each weight class
                                    const weightClassTotals = {};
                                    const weightClassActiveYears = {};
                                    let grandTotal = 0;
                                    let totalActiveWeightClassYears = 0;

                                    titleFightsByYearAndDivision.data.forEach(yearData => {
                                        Object.entries(yearData.divisions).forEach(([wc, count]) => {
                                            weightClassTotals[wc] = (weightClassTotals[wc] || 0) + count;
                                            grandTotal += count;
                                        });
                                        (yearData.active_weight_classes || []).forEach(wc => {
                                            weightClassActiveYears[wc] = (weightClassActiveYears[wc] || 0) + 1;
                                            totalActiveWeightClassYears++;
                                        });
                                    });
                                    
                                    return (
                                        <>
                                            {titleFightsByYearAndDivision.data.map((yearData) => {
                                                const yearTotal = Object.values(yearData.divisions).reduce((sum, count) => sum + count, 0);
                                                const activeWeightClasses = yearData.active_weight_classes || [];
                                                return (
                                                    <tr key={yearData.year} style={styles.tableRow}>
                                                        <td style={{ ...styles.tableCell }}>{yearData.year}</td>
                                                        {titleFightsByYearAndDivision.weight_classes.map(wc => {
                                                            const count = yearData.divisions[wc];
                                                            if (!count && !activeWeightClasses.includes(wc)) {
                                                                return (
                                                                    <td key={`${yearData.year}-${wc}`} style={{ ...styles.tableCell, textAlign: 'center', color: '#555' }} title="Inactive">
                                                                        –
                                                                    </td>
                                                                );
                                                            }
                                                            let textColor = '#ddd';
                                                            if (count === 1) textColor = '#E53935';
                                                            else if (count === 2) textColor = '#FFA726';
                                                            else if (count === 3) textColor = '#81C784';
                                                            else if (count >= 4) textColor = '#4CAF50';
                                                            
                                                            return (
                                                                <td key={`${yearData.year}-${wc}`} style={{ ...styles.tableCell, textAlign: 'center', color: textColor }}>
                                                                    {count || ''}
                                                                </td>
                                                            );
                                                        })}
                                                        <td style={{ ...styles.tableCell, textAlign: 'center', color: '#64B5F6' }}>
                                                            {yearTotal}
                                                        </td>
                                                        <td style={{ ...styles.tableCell, textAlign: 'center', color: '#aaa' }}>
                                                            {activeWeightClasses.length}
                                                        </td>
                                                        <td style={{ ...styles.tableCell, textAlign: 'center', color: activeWeightClasses.length > 0 ? getTitleFightRatioColor(yearTotal / activeWeightClasses.length) : '#ddd' }}>
                                                            {activeWeightClasses.length > 0 ? (yearTotal / activeWeightClasses.length).toFixed(2) : ''}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            <tr style={{ ...styles.tableRow, borderTop: '2px solid #555' }}>
                                                <td style={{ ...styles.tableCell, borderTop: '2px solid #555' }}>Total</td>
                                                {titleFightsByYearAndDivision.weight_classes.map(wc => {
                                                    const total = weightClassTotals[wc] || 0;
                                                    
                                                    return (
                                                        <td key={`total-${wc}`} style={{ ...styles.tableCell, textAlign: 'center', borderTop: '2px solid #555' }}>
                                                            {total || ''}
                                                        </td>
                                                    );
                                                })}
                                                <td style={{ ...styles.tableCell, textAlign: 'center', borderTop: '2px solid #555', color: '#64B5F6' }}>
                                                    {grandTotal}
                                                </td>
                                                <td style={{ ...styles.tableCell, borderTop: '2px solid #555' }}></td>
                                                <td style={{ ...styles.tableCell, borderTop: '2px solid #555' }}></td>
                                            </tr>
                                            <tr style={styles.tableRow}>
                                                <td style={{ ...styles.tableCell }} title="Title fights divided by the number of years the weight class was active">Per Active Year</td>
                                                {titleFightsByYearAndDivision.weight_classes.map(wc => {
                                                    const activeYears = weightClassActiveYears[wc] || 0;

                                                    return (
                                                        <td key={`per-active-${wc}`} style={{ ...styles.tableCell, textAlign: 'center' }}>
                                                            {activeYears > 0 ? ((weightClassTotals[wc] || 0) / activeYears).toFixed(2) : ''}
                                                        </td>
                                                    );
                                                })}
                                                <td style={styles.tableCell}></td>
                                                <td style={styles.tableCell}></td>
                                                <td style={{ ...styles.tableCell, textAlign: 'center', color: totalActiveWeightClassYears > 0 ? getTitleFightRatioColor(grandTotal / totalActiveWeightClassYears) : '#ddd' }}>
                                                    {totalActiveWeightClassYears > 0 ? (grandTotal / totalActiveWeightClassYears).toFixed(2) : ''}
                                                </td>
                                            </tr>
                                        </>
                                    );
                                })()}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={styles.loading}>No title fights data available</div>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: {
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    chartContainer: {
        background: '#1a1a1a',
        borderRadius: '12px',
        padding: '30px',
    },
    title: {
        color: '#fff',
        fontSize: '28px',
        marginBottom: '10px',
        textAlign: 'center',
        fontWeight: 'bold',
    },
    subtitle: {
        color: '#aaa',
        fontSize: '14px',
        textAlign: 'center',
        marginBottom: '10px',
    },
    note: {
        color: '#888',
        fontSize: '12px',
        textAlign: 'center',
        fontStyle: 'italic',
        marginBottom: '20px',
    },
    svg: {
        display: 'block',
        margin: '0 auto',
        background: '#1a1a1a',
        borderRadius: '8px',
        overflow: 'visible',
    },

    loading: {
        textAlign: 'center',
        color: '#4ECDC4',
        fontSize: '18px',
        padding: '40px',
    },
    error: {
        textAlign: 'center',
        color: '#FF6B6B',
        fontSize: '18px',
        padding: '40px',
    },
    tableContainer: {
        background: '#1a1a1a',
        borderRadius: '12px',
        padding: '30px',
        marginTop: '30px',
    },
    tableTitle: {
        color: '#fff',
        fontSize: '22px',
        marginBottom: '20px',
        textAlign: 'left',
        fontWeight: '600',
    },
    eliteThresholdsContainer: {
        background: '#252525',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '25px',
        border: '1px solid #444',
    },
    eliteThresholdsTitle: {
        color: '#fff',
        fontSize: '16px',
        marginBottom: '8px',
        fontWeight: '600',
    },
    eliteThresholdsSubtitle: {
        color: '#aaa',
        fontSize: '13px',
        marginBottom: '15px',
        fontStyle: 'italic',
    },
    eliteThresholdsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
        gap: '12px',
        marginBottom: '15px',
    },
    eliteThresholdGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    eliteThresholdLabel: {
        color: '#ccc',
        fontSize: '12px',
        fontWeight: '500',
    },
    eliteThresholdInput: {
        padding: '6px 8px',
        borderRadius: '4px',
        border: '1px solid #555',
        background: '#1a1a1a',
        color: '#ddd',
        fontSize: '13px',
        textAlign: 'center',
    },
    eliteThresholdsButtonGroup: {
        display: 'flex',
        gap: '10px',
        justifyContent: 'flex-start',
    },
    filterContainer: {
        display: 'flex',
        gap: '15px',
        marginTop: '30px',
        marginBottom: '10px',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
    },
    filterGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    filterLabel: {
        color: '#ccc',
        fontSize: '12px',
        fontWeight: '500',
        whiteSpace: 'nowrap',
    },
    filterInput: {
        padding: '4px 6px',
        borderRadius: '6px',
        border: '1px solid #444',
        background: '#2a2a2a',
        color: '#ddd',
        fontSize: '12px',
        width: '70px',
    },
    clearButton: {
        padding: '6px 12px',
        borderRadius: '6px',
        border: '1px solid #666',
        background: '#333',
        color: '#ccc',
        fontSize: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    applyButton: {
        height: 28,
        padding: '6px 12px',
        fontSize: '0.85em',
        fontWeight: 600,
        borderRadius: 6,
        border: '1px solid #888',
        background: '#232323',
        color: '#ddd',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        whiteSpace: 'nowrap',
    },
    tableWrapper: {
        overflowX: 'auto',
        borderRadius: '8px',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '13px',
    },
    tableHeader: {
        background: '#1a1a1a',
        borderBottom: '2px solid #444',
    },
    headerCell: {
        color: '#ccc',
        fontWeight: 'normal',
        textAlign: 'left',
        verticalAlign: 'bottom', // one-line headers line up with the second line of two-line ones
        padding: '6px 6px',
        userSelect: 'none',
        border: 'none',
        whiteSpace: 'nowrap',
    },
    tableCell: {
        color: '#ddd',
        padding: '4px 6px',
        border: 'none',
        borderBottom: '1px solid #444',
        whiteSpace: 'nowrap',
    },
    transparentCell: {
        color: '#ddd',
        padding: '4px 6px',
        border: 'none',
        borderBottom: '1px solid #444',
        opacity: 0.5,
        whiteSpace: 'nowrap',
    },
    eventNameCell: {
        color: '#ddd',
        padding: '4px 6px',
        border: 'none',
        borderBottom: '1px solid #444',
    },
    tableRow: {
        transition: 'background-color 0.2s',
        '&:hover': {
            background: '#333c4d !important',
        },
    },
    eventCount: {
        color: '#888',
        fontSize: '12px',
        marginTop: '15px',
        textAlign: 'right',
        fontStyle: 'italic',
    },
    link: {
        color: '#64B5F6',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'color 0.2s',
    },
    divisionToggleContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '20px',
        flexWrap: 'wrap',
    },
    divisionButtonInactive: {
        opacity: 0.6,
    },
    chartWithLegendContainer: {
        display: 'flex',
        gap: '20px',
        alignItems: 'flex-start'
    },
    seeMoreButton: {
        padding: '10px 20px',
        borderRadius: '6px',
        border: '1px solid #555',
        background: '#2a2a2a',
        color: '#ccc',
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontWeight: '500',
    },
    tableFootnote: {
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #444',
    },
    footnoteText: {
        color: '#888',
        fontSize: '12px',
        margin: '0',
        fontStyle: 'italic',
    },
};

// Add global styles for range slider
const sliderStyles = `
    input[type='range'] {
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2));
    }
    
    input[type='range']::-webkit-slider-thumb {
        appearance: none;
        -webkit-appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: linear-gradient(135deg, #64B5F6 0%, #5A9FE0 100%);
        cursor: pointer;
        border: 2px solid #1a1a1a;
        box-shadow: 0 2px 8px rgba(100, 181, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2);
        transition: all 0.15s ease;
    }
    
    input[type='range']::-webkit-slider-thumb:hover {
        box-shadow: 0 4px 12px rgba(100, 181, 246, 0.6), inset 0 1px 2px rgba(255, 255, 255, 0.3);
        transform: scale(1.1);
    }
    
    input[type='range']::-webkit-slider-thumb:active {
        box-shadow: 0 2px 8px rgba(100, 181, 246, 0.8), inset 0 1px 3px rgba(0, 0, 0, 0.3);
        transform: scale(0.95);
    }
    
    input[type='range']::-moz-range-thumb {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: linear-gradient(135deg, #64B5F6 0%, #5A9FE0 100%);
        cursor: pointer;
        border: 2px solid #1a1a1a;
        box-shadow: 0 2px 8px rgba(100, 181, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2);
        transition: all 0.15s ease;
    }
    
    input[type='range']::-moz-range-thumb:hover {
        box-shadow: 0 4px 12px rgba(100, 181, 246, 0.6), inset 0 1px 2px rgba(255, 255, 255, 0.3);
    }
    
    input[type='range']::-moz-range-thumb:active {
        box-shadow: 0 2px 8px rgba(100, 181, 246, 0.8), inset 0 1px 3px rgba(0, 0, 0, 0.3);
    }
    
    input[type='range']::-webkit-slider-runnable-track {
        background: linear-gradient(to right, #2a2a2a 0%, #2a2a2a 100%);
        height: 6px;
        border-radius: 3px;
        border: none;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.5), 0 1px 2px rgba(0, 0, 0, 0.3);
    }
    
    input[type='range']::-moz-range-track {
        background: #2a2a2a;
        height: 6px;
        border-radius: 3px;
        border: none;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.5), 0 1px 2px rgba(0, 0, 0, 0.3);
    }
    
    input[type='range']::-moz-range-progress {
        background: linear-gradient(to right, #64B5F6 0%, #5A9FE0 100%);
        height: 6px;
        border-radius: 3px;
        box-shadow: 0 0 8px rgba(100, 181, 246, 0.3);
    }
`;

if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = sliderStyles;
    if (!document.head.querySelector('style[data-range-slider]')) {
        style.setAttribute('data-range-slider', 'true');
        document.head.appendChild(style);
    }
}

export default EventsByYear;


